import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import * as Cesium from 'cesium';
import 'cesium/Build/Cesium/Widgets/widgets.css';
import type {
  ActiveFireFeature,
  AircraftFeature,
  AircraftTrackingStatus,
  CameraFeature,
  CameraStreamType,
  CapAlertFeature,
  HighRiskHotspotFeature,
  RawHotspotFeature,
  CameraSmokeFeature,
  UtilityOutageFeature,
  RoadClosureFeature,
  ArcgisFeature,
  OperatorEvacOrderFeature,
  CommsDegradationZoneFeature,
  Incident,
  ResponseAssetFeature,
  SensorFeature,
  StagingArea,
  Sector,
  WellnessCheckStatus,
} from '@tsr/shared-types';
import { ownershipHoverLabel } from './incidentVocabulary';
import { cameraConePolygon, cameraCoveragePolygon, clampUnknownDiscRange } from '@tsr/geo';
import { COVERAGE_LIVE, COVERAGE_SNAPSHOT, SENSOR_WEATHER } from '@tsr/ui';
import {
  fetchAircraft,
  fetchCameras,
  fetchResponseAssets,
  fetchSensors,
  type BboxQuery,
  type VesselFeature,
  type SmokeAnomalyFeature,
  type IceStormAnomalyFeature,
  type FloodAnomalyFeature,
  type HydrometricGaugeFeature,
  type DroneCoverageFeature,
  type CommunityFeature,
} from './api';
import { createFlyController, type FlyController, CANADA_VIEW_BOUNDS } from './cameraFly';
import { preferNonCoverageFillPick } from './coveragePick';
import { firstClaimedZonePick } from './zonePick';
import { pickFireUnderCoLocatedHotspot } from './layers/coLocatedOcFulSuppression';
import { computeFanOffsets, fanEnabledAtHeight, type ColocatedPoint } from './coLocatedClusters';
import type { ColocatedIncidentRef } from './SidePanel';
import { installFlythrough, type FlythroughController } from './flythrough';
import { installAarReplay, type AarReplayController, type AarReplayViewer } from './aarReplay';
import { loadHomeView } from './homeView';
import { clusterCamerasByFamily, metersPerPixel, type CameraCluster } from './cameraCluster';
import {
  cameraVisibilityFar,
  clusterVisibilityFarM,
  footprintAlpha,
  tierVisibleCameras,
  type FootprintShape,
} from './coneVisibility';
import {
  clusterRegister,
  greenBreatheSize,
  greenGlowAlpha,
  greenGlowSize,
  isLiveCamera,
  orderClustersLiveLast,
  urgentRedRegistry,
} from './greenMotion';
import { glowTextureDataUrl } from './glowTexture';
import { nextZoomHeight } from './mapControls';
import {
  installProvincialBoundaries,
  type BoundaryLayerHandle,
} from './layers/ProvincialBoundariesLayer';
import { installActiveFiresLayer, type ActiveFiresLayerHandle } from './layers/ActiveFiresLayer';
import {
  installHighRiskHotspotsLayer,
  type HighRiskHotspotsLayerHandle,
} from './layers/HighRiskHotspotsLayer';
import { installRawHotspotsLayer, type RawHotspotsLayerHandle } from './layers/RawHotspotsLayer';
import {
  installFusionCandidatesLayer,
  type FusionCandidatesLayerHandle,
} from './layers/FusionCandidatesLayer';
import { installCameraSmokeLayer, type CameraSmokeLayerHandle } from './layers/CameraSmokeLayer';
import { installPowerOutagesLayer, type PowerOutagesLayerHandle } from './layers/PowerOutagesLayer';
import { installRoadClosuresLayer, type RoadClosuresLayerHandle } from './layers/RoadClosuresLayer';
import {
  installArcgisCategoryLayers,
  type ArcgisCategoryLayersHandle,
} from './layers/ArcgisFeatureLayer';
import {
  installCommsDegradationZonesLayer,
  type CommsDegradationZonesLayerHandle,
} from './layers/CommsDegradationZonesLayer';
import {
  installExtremeWeatherWarningsLayer,
  type ExtremeWeatherWarningsLayerHandle,
} from './layers/ExtremeWeatherWarningsLayer';
import { installFwiHeatmapLayer, type FwiHeatmapLayerHandle } from './layers/FwiHeatmapLayer';
import {
  installWmsOverlays,
  type WmsOverlayConfig,
  type WmsOverlaysHandle,
} from './layers/WmsOverlayLayer';
import { WMS_OVERLAYS } from './layers/wmsOverlays';
import {
  installMarineVesselsLayer,
  type MarineVesselsLayerHandle,
} from './layers/MarineVesselsLayer';
import {
  installSmokeAnomaliesLayer,
  type SmokeAnomaliesLayerHandle,
} from './layers/SmokeAnomaliesLayer';
import {
  installIceStormAnomaliesLayer,
  type IceStormAnomaliesLayerHandle,
} from './layers/IceStormAnomaliesLayer';
import {
  installFloodAnomaliesLayer,
  type FloodAnomaliesLayerHandle,
} from './layers/FloodAnomaliesLayer';
import {
  installHydrometricGaugesLayer,
  type HydrometricGaugesLayerHandle,
} from './layers/HydrometricGaugesLayer';
import {
  installFloodInundationLayer,
  type FloodInundationLayerHandle,
} from './layers/FloodInundationLayer';
import {
  installCameraViewshedLayer,
  type CameraViewshedLayerHandle,
} from './layers/CameraViewshedLayer';
import {
  installDroneCoverageLayer,
  type DroneCoverageLayerHandle,
} from './layers/DroneCoverageLayer';
import { installCommunitiesLayer, type CommunitiesLayerHandle } from './layers/CommunitiesLayer';
import { installStagingAreasLayer, type StagingAreasLayerHandle } from './layers/StagingAreasLayer';
import { installAreaCommandLayer, type AreaCommandLayerHandle } from './layers/AreaCommandLayer';
import type { AreaCommandIdentity } from './areaCommandFirstNotice';
import { installSectorsLayer, type SectorsLayerHandle } from './layers/SectorsLayer';
import { installSectorDrawTool, type SectorDrawToolHandle } from './SectorDrawTool';
import {
  installOperatorEvacuationOrderLayer,
  type OperatorEvacOrderLayerHandle,
} from './layers/OperatorEvacuationOrderLayer';
import { installEvacuationDrawTool, type EvacuationDrawToolHandle } from './EvacuationDrawTool';
import { installCommunityPlaceTool, type CommunityPlaceToolHandle } from './CommunityPlaceTool';
import { installCityLabels, type CityLabelsHandle } from './layers/canadianCities';
import { assetDistanceFar, assetIcon, classifyAsset } from './assetStyles';
import {
  aircraftStatusAlpha,
  aircraftStatusSize,
  classifyAircraft,
  headingToBillboardRotation,
  AIRCRAFT_FLEET_GOLD,
  type AircraftKind,
} from './aircraftStyles';
import { aircraftSilhouetteUrl } from './aircraftSilhouettes';
import { renderLucideToDataUrl } from './iconRegistry';
import { planMotionSamples, type PositionAtTime } from './aircraftInterp';
import { aircraftModelUri } from './aircraftModels';
import { isDemoMode } from './demoMode';
import {
  applyRenderRatio,
  resolveRenderResolution,
  watchDevicePixelRatio,
  type ResolvedRenderResolution,
} from './renderResolution';
import { generateSyntheticAircraft, type SyntheticOptions } from './aircraftSynthetic';
import { EMPTY_ASSET_COUNTS, type AssetCounts } from './prefs';
import { logAction } from './lib/operatorActions';
import { installRenderErrorGuard } from './cesiumRenderGuard';
import { installOcclusionImageryPump } from './cesiumOcclusionPump';
import { installImageryReadiness } from './cesiumImageryReadiness';
import { GlobeBootOverlay } from './GlobeBootOverlay';

// isLiveCamera moved to greenMotion.ts (#255) — cluster classification and the
// single-camera render share one live-family definition.

/** A probed snapshot URL that isn't HEALTHY (404/timeout/placeholder/etc.).
 *  Such markers are dimmed + gray-bordered so operators can see "broken source"
 *  at a glance without it being hidden (Phase Q.1 §2.2). null = never probed. */
function isUrlUnhealthy(f: CameraFeature): boolean {
  const h = f.properties.url_health;
  return !!h && h.status !== 'HEALTHY';
}

/** Gray border for broken-source markers. */
const UNHEALTHY_OUTLINE = '#94a3b8';
const UNHEALTHY_ALPHA = 0.4;

// Camera Video-glyph overlay (Q.1 Stage 4): cameras now read AS cameras — a
// white Lucide `video` glyph sits centred on the coloured dot. Live cams get a
// slightly larger glyph (the dot beneath it carries the V.3 pulse); snapshot
// cams a smaller one over a static blue dot.
const CAM_GLYPH_SIZE_LIVE = 12;
const CAM_GLYPH_SIZE_SNAPSHOT = 9;
/** Static snapshot dot, bumped from the old 10 px so it reads as a solid dot
 *  beneath the glyph (live dots keep the V.1 breathing pulse). */
const SNAPSHOT_DOT_SIZE = 14;
/** Base size of the live inner dot — the green breathe modulates ±10% around
 *  this (Law 6 family rule; see greenMotion.greenBreatheSize). */
const LIVE_DOT_BASE_SIZE = 10;
const CAM_GLYPH_COLOR = '#ffffff';

// Camera clustering (#195, control-panel spec §1). Nearby cameras collapse into
// one ambient count marker; the merge radius is the ground distance under this
// many screen pixels, so it shrinks as the operator zooms in and clusters
// dissolve on the way down. Co-located cams (one dock / one yard) stay grouped at
// any zoom and open a rail pick-list instead.
const CLUSTER_PIXEL_RADIUS = 34;
/** Members within this ground spread are "one site" — zoom can't separate them. */
const CO_LOCATED_RADIUS_M = 250;
/** Floor on the merge radius so a tiny on-screen extent still groups co-located
 *  cams at street zoom (keeps the dock-of-3 a cluster, never 3 stacked dots). */
const MIN_CLUSTER_RADIUS_M = 40;
/** Cluster marker uses the snapshot-camera blue so the count reads as cameras;
 *  ambient (no pulse), colour paired with the count label per the motion law. */
const CLUSTER_DOT_SIZE = 18;

/**
 * Marker + coverage polygon share one colour per source family so the
 * visual identity is consistent: "blue dot + blue cone = snapshot camera",
 * "green dot + green cone = live camera" (Phase D).
 */
function coverageColorFor(streamType: CameraStreamType): string {
  if (streamType === 'snapshot') return COVERAGE_SNAPSHOT;
  // 'hls' | 'mjpeg' | 'rtsp' | 'mp4_clip' all roll up to the live-stream family.
  return COVERAGE_LIVE;
}

interface MapViewProps {
  showCameras: boolean;
  showSensors: boolean;
  smokePriorityOnly: boolean;
  /** Response-asset sublayer toggles (R.1). */
  showFireStations: boolean;
  showWildfireBases: boolean;
  showAviationBases: boolean;
  showOntarioCorps: boolean;
  /** Partner names hidden within the Ontario Corps illustrative layer. */
  hiddenPartners: ReadonlySet<string>;
  /** Live ADS-B aircraft layer (R.1b). */
  showLiveAircraft: boolean;
  /** aircraft_role values hidden within the Live Aircraft layer. */
  hiddenAircraftRoles: ReadonlySet<string>;
  /** Reported active wildland fires — true when either wildfire layer is on
   *  (L.0 "Reported OC Wildfires" or Weather "All Reported Wildfires"). */
  showFireRisk: boolean;
  /** Canonical stage filter passed to /api/active-fires. `undefined` = all stages
   *  (All Reported Wildfires); `['OC']` = out-of-control only (L.0 layer 4). */
  fireStages?: string[];
  /** response_type filter passed to /api/active-fires. `undefined` = all OC postures
   *  (both the Default OC Layer 4 and the all-stages layer pass undefined since #607/#621 —
   *  the client LOD, not a response_type filter, governs visibility). A concrete value
   *  only comes from fixtures/tests. */
  fireResponseTypes?: string[];
  /** AI-fused high-risk hotspots — L.0 layer 2 (E.2.3), default ON. */
  showHighRiskHotspots: boolean;
  /** Raw pre-fusion satellite thermal anomalies (E.W.0.5, Fire Risk group). The
   *  toggle defaults ON, but the layer self-gates rendering to regional+ zoom. */
  showRawHotspots: boolean;
  /** Fusion Candidates (#370, Fire Risk group) — below-urgent fused hotspots
   *  (Response Level 1/2). Default OFF; the layer self-gates to regional+ zoom. */
  showFusionCandidates: boolean;
  /** Camera Smoke Detections (E.W.5, Fire Risk group) — pre-fusion camera-CV smoke.
   *  Default OFF; the layer self-gates rendering to regional+ zoom. */
  showCameraSmoke: boolean;
  /** Power Outages (Ice Storm Risk group) — the utility_outages render. Default
   *  OFF; the layer self-gates rendering to regional+ zoom. */
  showPowerOutages: boolean;
  /** Road Closures (Ice Storm Risk group) — the road_closures render. Default OFF;
   *  the layer self-gates rendering to regional+ zoom. */
  showRoadClosures: boolean;
  /** Road Closures "Restrictions" mute (L.1 sub-toggle): false mutes lane/partial
   *  restriction dots so only full closures render. Default true. */
  roadClosuresRestrictions: boolean;
  /** Estimated Comms Degradation (Ice Storm Risk group) — the #109
   *  comms_degradation_zones render. Default OFF; self-gates to regional+ zoom. */
  showCommsDegradationZones: boolean;
  /** Hydrometric Gauges (Flood Risk group) — the hydrometric_observations render.
   *  Default OFF; the layer self-gates rendering to regional+ zoom. */
  showHydrometricGauges: boolean;
  /** 3D Flood Inundation (Flood Risk group, #177) — modelled water surface + 3D
   *  terrain/buildings over the bounded Kashechewan demo region. Default OFF. */
  showFloodInundation: boolean;
  /** Viewshed-on-select (#262, founder ruling) — selecting a camera auto-drapes
   *  its #178 terrain LOS footprint (async; released on deselect). This is the
   *  per-operator "hide viewshed" pref, default ON; NOT a standing layer. */
  viewshedOnSelect: boolean;
  /** The camera the viewshed drapes for (App's selected camera, or null).
   *  Selecting computes async (terrain hold acquired); null releases both. */
  viewshedCamera: CameraFeature | null;
  /** Active Severe/Extreme CAP polygons — the second source of the multi-source
   *  Layer #2 surface (EWW). Effective = `urgentAnomalies && extremeWeatherWarnings`
   *  (the Weather mute toggle subtracts only this subset). */
  showExtremeWeatherWarnings: boolean;
  /** Per-category visibility for the generalized public-ArcGIS renderer (#213) —
   *  keyed by arcgis_public_features `category` (evacuation, fire_restriction,
   *  drought, public_health, emergency_declaration, hurricane). Each default OFF; the
   *  layers self-gate to regional+ zoom. */
  arcgisCategoryVisibility: Record<string, boolean>;
  /** CWFIS FWI heatmap imagery overlay (Weather group, radio-exclusive, E.2.2). */
  showFwiHeatmap: boolean;
  /** Per-key visibility for the WMS raster overlays (#212) — keyed by layer key
   *  (eccc-hurricane-track, nrcan-flood-extent). Each default OFF; ambient. */
  wmsOverlayVisibility: Record<string, boolean>;
  /** Extra WMS overlay configs to mount BEYOND the COP registry — the WALL's
   *  GeoMet radar (#272, PART XI Ruling 1.4: a wall overlay, never a COP layer
   *  row). Driven by the same wmsOverlayVisibility map. Read once at viewer
   *  init (like the registry); the operator COP omits it. */
  extraWmsOverlays?: readonly WmsOverlayConfig[];
  /** Fires when a self-refreshing WMS overlay (radar) re-fetches — feeds the
   *  wall's honest "RADAR · refreshed HH:MM" timestamp line (Ruling 1.4d). */
  onWmsOverlayRefresh?: (key: string, atMs: number) => void;
  /** WALL profile (#272, PART XI Rulings 1.3 + 1.6): lift the Law-3 acked
   *  country-zoom drop (dot anomalies) and the #153 acked-CAP recede gate
   *  (EWW polygons) so acknowledged-but-live situations stay visible at every
   *  wall zoom as ACK-STEEL. Default false — the operator COP is untouched. */
  liftAckedZoomGates?: boolean;
  /** Provincial boundary polylines (Context group) — base-map furniture, on by
   *  default but operator-toggleable (L.1). */
  showProvincialBoundaries: boolean;
  /** Drone Operator Coverage (Asset group, L.2.2 §3B.6). */
  showDroneCoverage: boolean;
  /** Staging Areas (Asset group, R.4.2 §2.5/§6.4). */
  showStagingAreas: boolean;
  /** Area Command oversight markers (Asset group, R5 Slice 3 #507, IMS §4.6). */
  showAreaCommand: boolean;
  /** W.2 — Vulnerable Communities (Flood Risk + Ice Storm Risk groups). True when
   *  EITHER toggle is on (both back the same hazard-agnostic community layer). */
  showCommunities: boolean;
  /** W.2 — per-community wellness status + headcount chip, supplied by App's
   *  client-side detail cache (the W.1 list endpoint omits them). Drives marker
   *  colour + the regional-zoom badge; a change triggers an in-place recolour. */
  communityStatuses: Record<string, { status: WellnessCheckStatus | null; badge: string | null }>;
  /** #189 — the default-OFF "Vulnerability shading" sub-toggle: appends the derived
   *  social-vulnerability band to the community marker chip (ambient tint + label).
   *  A change re-applies in place via recolor() — no refetch. */
  communityVulnerabilityShading: boolean;
  /** Selected incident point (lat/lon) for the drone reachability highlight; null
   *  when the current selection is not an incident (or nothing is selected). */
  selectedIncidentPoint?: { lat: number; lon: number } | null;
  onSelectCamera: (c: CameraFeature | null) => void;
  /** #195 — a co-located camera cluster (zoom can't separate them) was clicked;
   *  drop a selectable "N cameras here → pick one" list into the persistent rail. */
  onSelectCameraCluster: (cameras: CameraFeature[]) => void;
  /** #415 (Phase 6b) — N≥2 ACT incidents collided at the clicked pixel; open the
   *  disambiguation list (the incident sibling of onSelectCameraCluster). */
  onSelectIncidentCluster: (incidents: ColocatedIncidentRef[]) => void;
  onSelectSensor: (s: SensorFeature | null) => void;
  onSelectAsset: (a: ResponseAssetFeature | null) => void;
  onSelectAircraft: (a: AircraftFeature | null) => void;
  onSelectFire: (f: ActiveFireFeature | null) => void;
  onSelectHighRiskHotspot: (h: HighRiskHotspotFeature | null) => void;
  /** E.W.0.5 — raw hotspot selection (ambient; no incident fly). */
  onSelectRawHotspot: (h: RawHotspotFeature | null) => void;
  /** E.W.5 — camera smoke selection (ambient; no incident fly). */
  onSelectCameraSmoke: (c: CameraSmokeFeature | null) => void;
  /** Power Outages — outage selection (ambient; no incident fly). */
  onSelectUtilityOutage: (o: UtilityOutageFeature | null) => void;
  /** Road Closures — closure selection (ambient; no incident fly). */
  onSelectRoadClosure: (c: RoadClosureFeature | null) => void;
  /** Public-ArcGIS category feature selection (#213; evac + fire bans / drought /
   *  boil-water / SoLE / cyclones) — ambient context; no incident fly. */
  onSelectArcgisFeature: (f: ArcgisFeature | null) => void;
  /** Estimated Comms Degradation — zone selection (ambient; no incident fly). */
  onSelectCommsDegradationZone: (z: CommsDegradationZoneFeature | null) => void;
  onSelectCapAlert: (c: CapAlertFeature | null) => void;
  /** L.2.2 — marine vessel / drone coverage / smoke_detected anomaly selections. */
  onSelectMarineVessel: (v: VesselFeature | null) => void;
  onSelectDroneCoverage: (d: DroneCoverageFeature | null) => void;
  /** R.4.2 — staging-area marker selection. */
  onSelectStagingArea: (s: StagingArea | null) => void;
  /** R5 Slice 3 (#507) — Area Command marker click → the parent incident's expand
   *  panel (the {kind:'incident'} SidePanel arm; same open path as the rail). */
  onSelectAreaCommand: (inc: Incident) => void;
  /** Task #64 — Sectors layer toggle (Asset group). */
  showSectors: boolean;
  /** Task #64 — the incident whose sectors render (null = none). Sectors are
   *  incident-scoped, not bbox-driven, so this drives setIncident on the layer. */
  sectorIncidentId: string | null;
  /** Task #64 — sector polygon selection (click → detail panel + rollup). */
  onSelectSector: (s: Sector | null) => void;
  /** Task #64 — a freshly-drawn polygon ring (closed [lon,lat][]); App turns it
   *  into a named sector via POST /api/sectors. */
  onSectorDrawn: (ring: Array<[number, number]>) => void;
  /** Task #64 — sector render count (optional, for toggle labels). */
  onSectorCountChange?: (count: number) => void;
  /** #445-(3) — operator-declared evac-order layer visibility (default OFF). */
  showOperatorEvacOrders: boolean;
  /** #445-(3) — a freshly-drawn evacuation-area ring (closed [lon,lat][]); App turns
   *  it into a recorded order via the declare modal + POST /api/evacuation-orders. */
  onEvacOrderDrawn: (ring: Array<[number, number]>) => void;
  /** #445-(3) — an operator evac order clicked on the map → its detail panel. */
  onSelectOperatorEvacOrder: (f: OperatorEvacOrderFeature | null) => void;
  onSelectSmokeAnomaly: (s: SmokeAnomalyFeature | null) => void;
  /** Ice Storm Fusion Anomaly selection (§4.5; a queue incident — flies in). */
  onSelectIceAnomaly: (i: IceStormAnomalyFeature | null) => void;
  /** Flood Fusion Anomaly selection (§4.4; a queue incident — flies in). */
  onSelectFloodAnomaly: (f: FloodAnomalyFeature | null) => void;
  /** Hydrometric gauge selection (ambient Flood Risk layer; no incident fly). */
  onSelectHydrometricGauge: (g: HydrometricGaugeFeature | null) => void;
  /** W.2 — community marker selection. */
  onSelectCommunity: (c: CommunityFeature | null) => void;
  /** W.2 editor — a point placed on the map ([lon, lat]); App turns it into a
   *  declared community or a centroid move per the active intent. */
  onCommunityPlaced: (lonLat: [number, number]) => void;
  /** W.2 editor — the place-a-point tool started (true) / stopped (false, incl.
   *  ESC / right-click cancel), so App can sync its toolbar button + hint. */
  onCommunityPlaceStateChange?: (active: boolean) => void;
  /** W.2 — reports the community ids currently rendered, so App can lazily fill
   *  its status/badge cache. */
  onCommunitiesInView?: (ids: string[]) => void;
  onCameraCountChange: (count: number, loading: boolean) => void;
  onSensorCountChange: (count: number, loading: boolean) => void;
  onAssetCountsChange: (counts: AssetCounts) => void;
  onAircraftCountChange: (count: number) => void;
  onFireCountChange: (count: number) => void;
  onHighRiskCountChange: (count: number) => void;
  /** E.W.0.5 — raw hotspot render count (optional, for the toggle label). */
  onRawHotspotCountChange?: (count: number) => void;
  /** E.W.5 — camera smoke render count (optional, for the toggle label). */
  onCameraSmokeCountChange?: (count: number) => void;
  /** Power Outages render count (optional, for the toggle label). */
  onUtilityOutageCountChange?: (count: number) => void;
  /** #337 — road-closure in-view count (optional). Feeds the honest data-coverage
   *  cue: a covered view with a KNOWN zero reads "no closures reported", distinct
   *  from an out-of-coverage view's "no feed here" (unknown ≠ zero). Emits **null**
   *  when the count is UNKNOWN for the current view (fetch failed) so a stale count
   *  can't survive an outage as a false "feed is live". */
  onRoadClosureCountChange?: (count: number | null) => void;
  /** L.2.2 layer counts (optional — used for toggle labels / queue chips). */
  onVesselCountChange: (count: number) => void;
  onDroneCoverageCountChange: (count: number) => void;
  /** R.4.2 — staging-area render count (optional, for toggle labels). */
  onStagingAreaCountChange?: (count: number) => void;
  /** R5 Slice 3 (#507) — live open-Area-Command count + identities. Feeds the layer-picker
   *  row ("Area Command (N)") even while the layer is OFF — the PEOC first-notice cue — and
   *  the identities drive the #509 first-notice toast naming a newly-established Area Command. */
  onAreaCommandCountChange?: (count: number, areaCommands: AreaCommandIdentity[]) => void;
  onSmokeAnomalyCountChange: (count: number) => void;
  /** Ice Storm Fusion Anomaly render count (optional, for the queue chip). */
  onIceAnomalyCountChange: (count: number) => void;
  /** Flood Fusion Anomaly render count (optional, for the queue chip). */
  onFloodAnomalyCountChange?: (count: number) => void;
  /** Hydrometric gauge render count (optional, for the toggle label). */
  onHydrometricGaugeCountChange?: (count: number) => void;
  /** W.2 — community render count (optional, for toggle labels). */
  onCommunityCountChange?: (count: number) => void;
  /** Current map-view bbox, reported on each camera settle — drives the
   *  "Current map view" SITREP scope (SITREP.1.5). */
  onBboxChange?: (bbox: BboxQuery) => void;
  /** Populated by MapView once the Cesium viewer exists, so App can fly the
   *  camera to a marker (click-to-fly, Phase V). Cleared on unmount. */
  flyControllerRef: MutableRefObject<FlyController | null>;
  /** Task #64 — populated by MapView so App's "Draw Sector" button can start /
   *  cancel the polygon draw tool and refresh the sectors layer after a mutation. */
  sectorControllerRef: MutableRefObject<SectorController | null>;
  /** #445-(3) — populated by MapView so App's CommunityBody "Record evacuation order"
   *  button can start / cancel the evac-area draw tool + refresh the operator-evac
   *  layer after a declare. */
  evacuationOrderControllerRef: MutableRefObject<EvacuationOrderController | null>;
  /** W.2 editor — populated by MapView so App can start / cancel the place-a-point
   *  tool (declare / move) and refresh the communities layer after a mutation. */
  communityControllerRef: MutableRefObject<CommunityController | null>;
  /** #177 — populated by MapView so the FloodInundationControl can drive the
   *  modelled water level + fly to the demo region without re-rendering MapView. */
  floodInundationControllerRef: MutableRefObject<FloodInundationController | null>;
  /** #179 — populated by MapView so the incident/community detail panels can
   *  trigger the operator 3D flythrough (photoreal tiles stream on demand and
   *  are released when it ends). Optional — the WALL renders without it. */
  flythroughControllerRef?: MutableRefObject<FlythroughController | null>;
  /** #180 — populated by MapView so an incident panel can enter the AAR time
   *  replay (historical anomaly recolours + event highlight; scrub-only).
   *  Optional — the WALL renders without it. */
  aarReplayControllerRef?: MutableRefObject<AarReplayController | null>;
  /** Camera ids with an open pop-out window — their markers pulse (Phase V). */
  pulsingCameraIds: ReadonlySet<string>;
  /** WALL render profile (#132, council Part V): multiply Tier-0/1 marker geometry
   *  (anomaly dots/rings, wildfire glyphs, camera dots/glyphs) so they read from
   *  across a room. Default 1 — the operator COP is byte-for-byte unchanged. */
  markerScale?: number;
  /** WALL render profile (#132): when set, re-poll every visible Tier-0/1 layer on
   *  this interval (ms) so a static, unattended wall stays live without a map nudge.
   *  Unset (operator COP) → only the existing 45s anomaly-lifecycle poll runs. */
  autoRefreshMs?: number;
}

/** Imperative handle MapView exposes to App for the #177 flood-inundation control:
 *  set the modelled water depth (m above the sampled ground baseline) + fly to the
 *  demo region. Lets the slider drive the layer without re-rendering MapView. */
export interface FloodInundationController {
  setLevelDepth(depthM: number): void;
  /** Switch the active demo region (Kashechewan ↔ Calgary high-rise) — #220. */
  setPreset(presetId: string): void;
  flyToDemo(): void;
  isReady(): boolean;
}

/** Imperative handle MapView exposes to App for the sector draw tool + layer. */
export interface SectorController {
  startDraw(): void;
  cancelDraw(): void;
  isDrawing(): boolean;
  /** Re-fetch the current incident's sectors (after a draw / edit / close). */
  refreshSectors(): void;
}

/** #445-(3) — imperative handle MapView exposes to App for the operator-declared
 *  evac-order draw tool + layer refresh (draw → declare → render). */
export interface EvacuationOrderController {
  startDraw(): void;
  cancelDraw(): void;
  isDrawing(): boolean;
  /** #457 — preview a generated (one-click) evacuation ring + fly to frame it. */
  previewRing(ring: Array<[number, number]>): void;
  /** #457 — clear the generated-zone preview (on submit / cancel / redraw). */
  clearPreview(): void;
  /** Re-fetch the operator-declared evac orders (after a declare / escalate / rescind). */
  refreshOrders(): void;
}

/** Imperative handle MapView exposes to App for the W.2 community place-a-point
 *  tool + layer refresh (declare / move / retire). */
export interface CommunityController {
  startPlace(): void;
  cancelPlace(): void;
  isPlacing(): boolean;
  /** Re-fetch the in-view communities (after a declare / move / retire). */
  refreshCommunities(): void;
}

const DEBOUNCE_MS = 500;
/** Hard cap on per-cone radius regardless of fov_inference output, so the
 *  occasional 5 km estimate doesn't paint a city-wide disc. */
const MAX_FOOTPRINT_RANGE_M = 5000;
// PTZ_DISC_ALPHA + the footprintAlpha() shape/health/hover logic live in
// ./coneVisibility (Task #48) so the alpha rules are unit-tested without Cesium.

// --- Live aircraft (R.1b; 3D wave D2 §3.3) ---
// Billboard pixel size is no longer a flat constant: it steps by tracking tier
// (#265 founder size-tier ruling) via aircraftStatusSize() and multiplies by the
// WALL markerScale at render time — see the billboard width/height callbacks.
/** Re-poll the aircraft API on this cadence so new server truths arrive
 *  independent of camera moves. */
const AIRCRAFT_POLL_MS = 30_000;
/** Elevation for an aircraft whose ADS-B altitude is unknown, so the icon still
 *  floats off the terrain ("elevated icons") instead of clamping to the ground. */
const DEFAULT_AIRCRAFT_ALTITUDE_M = 1500;
/** Keep this much sample history behind "now" so VelocityOrientationProperty can
 *  derive heading and Cesium has bracketing samples; older samples are trimmed. */
const AIRCRAFT_HISTORY_SECONDS = 30;
// LOD (true-3D glTF vs billboard silhouette). A glTF model is shown only when the
// camera is zoomed in close to an aircraft AND the on-screen model budget allows;
// everything else renders the cheap billboard. Bounds the glTF count so framerate
// holds with many aircraft.
/** Max simultaneous glTF models (nearest-N within range win). */
const AIRCRAFT_MODEL_BUDGET = 24;
/** Only a candidate for the 3D model when the camera is within this range (m). */
const AIRCRAFT_MODEL_RANGE_M = 80_000;
/** Throttle LOD re-evaluation (aircraft + camera both move). */
const AIRCRAFT_LOD_INTERVAL_MS = 250;
/** Re-poll the Anomaly-Queue map layers on this cadence so the Colour Law 3
 *  lifecycle surfaces on a STABLE view without a camera nudge: an ack made in the
 *  queue recolours the marker to ACK-STEEL, a #129 re-alarm returns it to
 *  URGENT-RED, and a cleared marker fades to its grey ghost. The fetch reuses the
 *  current view rectangle, so the same bbox key lets the ghost tracker fire. */
const ANOMALY_POLL_MS = 45_000;

interface CameraEntry {
  feature: CameraFeature;
  /** Inner marker dot (the clickable camera point). */
  marker: Cesium.Entity;
  /** Either a directional cone (heading_known=true) or an
   *  omnidirectional coverage disc (heading_known=false). */
  footprint: Cesium.Entity;
  /** Outer glow halo, live cameras only (null for snapshot cams). */
  glow: Cesium.Entity | null;
  /** White Video-glyph billboard overlay (Stage 4); null only when no canvas is
   *  available to rasterise it (unit tests). */
  glyph: Cesium.Entity | null;
  /** `far` distance for this camera's distanceDisplayCondition, reused when a
   *  pop-out ring is spawned so the ring culls with its marker. */
  far: number;
}

interface SensorEntry {
  feature: SensorFeature;
  marker: Cesium.Entity;
}

interface AircraftEntry {
  feature: AircraftFeature;
  entity: Cesium.Entity;
  /** Clock-driven interpolated position (D2 §3.3). Fed time-tagged samples per
   *  fix; Cesium interpolates between them at framerate and HOLDs past the last. */
  position: Cesium.SampledPositionProperty;
  headingDeg: number | null;
  speedKt: number | null;
  /** Last known altitude (m) — reused for the held position when a fix lands. */
  altM: number;
  /** Server truth timestamp; re-sample only when this advances (avoids snap-back). */
  lastSeenAt: string | null;
  /** Tracking tier: live (full) / stale (faded) / based (dimmest, persistent). */
  status: AircraftTrackingStatus;
  /** Airframe kind — selects the silhouette + glTF archetype for the LOD swap. */
  kind: AircraftKind;
  /** Current LOD: true = 3D model shown, false = billboard silhouette. */
  modelShown: boolean;
}

function rectangleToBbox(rect: Cesium.Rectangle): BboxQuery {
  return {
    west: Cesium.Math.toDegrees(rect.west),
    south: Cesium.Math.toDegrees(rect.south),
    east: Cesium.Math.toDegrees(rect.east),
    north: Cesium.Math.toDegrees(rect.north),
  };
}

/**
 * URL hash override for the initial camera position. Useful for sharing
 * specific views and for deterministic screenshots:
 *   #at=43.7,-79.4,120000  → lat, lon, heightM
 */
function parseInitialCameraFromHash(): { lat: number; lon: number; heightM: number } | null {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash;
  // Tolerant of trailing &params (e.g. #at=...&asset=...) so the view hash can
  // be combined with #cam= / #asset= deep links.
  const m = /[#&]at=([-\d.]+),([-\d.]+),([\d.]+)/.exec(hash);
  if (!m) return null;
  const [, lat, lon, h] = m;
  const out = { lat: Number(lat), lon: Number(lon), heightM: Number(h) };
  if (![out.lat, out.lon, out.heightM].every((n) => Number.isFinite(n))) return null;
  return out;
}

/** True when the URL carries an entity deep-link (#cam / #fire / #hotspot /
 *  #evacuation / #asset) that App resolves + flies to after mount. The cold-start
 *  default-view logic (#138/#149) defers to those so a shared link isn't yanked
 *  to Canada or a saved home before the entity flight runs. */
function hasEntityDeepLink(): boolean {
  if (typeof window === 'undefined') return false;
  return /[#&](cam|fire|hotspot|evacuation|asset)=/.test(window.location.hash);
}

export function MapView(props: MapViewProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const propsRef = useRef(props);
  propsRef.current = props;
  const applyVisibilityRef = useRef<() => void>(() => undefined);
  const refreshRef = useRef<() => void>(() => undefined);
  const pulseRef = useRef<() => void>(() => undefined);
  const refreshAssetsRef = useRef<() => void>(() => undefined);
  const applyAssetVisibilityRef = useRef<() => void>(() => undefined);
  const refreshAircraftRef = useRef<() => void>(() => undefined);
  const applyAircraftVisibilityRef = useRef<() => void>(() => undefined);
  const applyFireVisibilityRef = useRef<() => void>(() => undefined);
  const applyHighRiskVisibilityRef = useRef<() => void>(() => undefined);
  const applyRawHotspotsVisibilityRef = useRef<() => void>(() => undefined);
  const applyFusionCandidatesVisibilityRef = useRef<() => void>(() => undefined);
  const applyCameraSmokeVisibilityRef = useRef<() => void>(() => undefined);
  const applyPowerOutagesVisibilityRef = useRef<() => void>(() => undefined);
  const applyRoadClosuresVisibilityRef = useRef<() => void>(() => undefined);
  const applyRoadClosuresRestrictionsRef = useRef<() => void>(() => undefined);
  const applyCommsDegradationVisibilityRef = useRef<() => void>(() => undefined);
  const applyExtremeWeatherVisibilityRef = useRef<() => void>(() => undefined);
  const applyArcgisVisibilityRef = useRef<() => void>(() => undefined);
  const applyIncidentScopedLayersRef = useRef<() => void>(() => undefined);
  const applyBoundaryVisibilityRef = useRef<() => void>(() => undefined);
  const applyFwiVisibilityRef = useRef<() => void>(() => undefined);
  const applyWmsOverlaysVisibilityRef = useRef<() => void>(() => undefined);
  const applySmokeAnomalyVisibilityRef = useRef<() => void>(() => undefined);
  const applyIceAnomalyVisibilityRef = useRef<() => void>(() => undefined);
  const applyFloodAnomalyVisibilityRef = useRef<() => void>(() => undefined);
  const applyHydrometricGaugesVisibilityRef = useRef<() => void>(() => undefined);
  const applyFloodInundationVisibilityRef = useRef<() => void>(() => undefined);
  const applyCameraViewshedVisibilityRef = useRef<() => void>(() => undefined);
  const applyViewshedCameraRef = useRef<() => void>(() => undefined);
  const applyMarineVisibilityRef = useRef<() => void>(() => undefined);
  const applyDroneCoverageVisibilityRef = useRef<() => void>(() => undefined);
  const applyCommunityVisibilityRef = useRef<() => void>(() => undefined);
  const recolorCommunitiesRef = useRef<() => void>(() => undefined);
  const applyStagingAreasVisibilityRef = useRef<() => void>(() => undefined);
  const applyAreaCommandVisibilityRef = useRef<() => void>(() => undefined);

  // #259 — cold-boot loading chrome: false until base imagery has genuinely
  // drained for the first view (or the GlobeBootOverlay fallback cap fires).
  // Flipping it only re-renders the overlay; the mount-once viewer effect below
  // (deps []) never re-runs.
  const [imageryReady, setImageryReady] = useState(false);
  // OWNERSHIP HOVER (feat/ownership-coordination) — hovering an owned fire reveals the lead
  // organization + the incident's Response Level without a click, at ANY zoom (the owner-tag Label
  // only appears at regional+). Cursor-anchored; cleared when the cursor leaves an owned hazard.
  const [ownerTip, setOwnerTip] = useState<{ text: string; x: number; y: number } | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const token = import.meta.env.VITE_CESIUM_ION_TOKEN as string | undefined;
    if (token) Cesium.Ion.defaultAccessToken = token;

    const viewer = new Cesium.Viewer(container, {
      // Keep the WebGL backbuffer readable so canvas.toDataURL() can grab a
      // frame — pairs with the #at= hash override for deterministic,
      // shareable map screenshots. Negligible cost at our entity counts.
      contextOptions: { webgl: { preserveDrawingBuffer: true } },
      timeline: false,
      animation: false,
      baseLayerPicker: false,
      geocoder: false,
      homeButton: false,
      sceneModePicker: false,
      navigationHelpButton: false,
      fullscreenButton: false,
      infoBox: false,
      selectionIndicator: false,
      // Task #227: suppress Cesium's full-canvas red "render loop errors" panel.
      // On an uncaught render exception (e.g. one undecodable imagery tile /
      // billboard texture) Cesium freezes the loop AND paints this panel over the
      // globe. We recover the loop via installRenderErrorGuard below, so the panel
      // would only mask an already-recovered COP — drop it.
      showRenderLoopErrors: false,
    });
    // Task #227: a single undecodable image must NEVER black out the whole COP.
    // Cesium's default loop is fail-closed (it halts + raises renderError on any
    // render throw); this re-arms it so a bad frame degrades to "no thumbnail"
    // for that element instead of a dead globe. Bounded so a deterministic fault
    // can't spin the CPU. Install before any first paint / deep-link flight.
    installRenderErrorGuard(viewer);
    // Task #270: Chrome stops rAF for occluded/hidden windows, which freezes
    // Cesium's render loop and with it ALL imagery-tile dispatch — a wall
    // loaded under another window sits on a black globe indefinitely. The pump
    // drives work-gated off-rAF renders while hidden (≈1/s for the first
    // 5 min, ~1/min under intensive throttling after that — accepted, not
    // evaded) and bursts one render on reveal so dispatch resumes instantly.
    // Zero renders while hidden once the tile queue drains.
    const occlusionPump = installOcclusionImageryPump(viewer);
    // #259: watch base-imagery readiness so the cold-boot loading chrome fades on
    // a REAL tile-queue drain, not a timer. Observe-only — continuous render
    // (requestRenderMode off) streams the tiles; this just reports when they land.
    const imageryReadiness = installImageryReadiness(viewer, () => setImageryReady(true));
    // #309: flip the globe to native device-pixel-ratio so the WHOLE COP
    // (basemap, billboards, labels, polylines) renders crisp on retina/hi-DPI
    // instead of Cesium's default 1× (useBrowserRecommendedResolution ignores
    // window.devicePixelRatio). Behind a measured perf gate — operator-COP and
    // wall default OFF, demo force ON, localStorage/?nativeDpr opt-in, capped at
    // 2× — and re-applied on a display-density change (window dragged between
    // monitors). The occlusion pump + render guard are orthogonal: they drive
    // renders; the resolution only sets how large each rendered backbuffer is.
    const writeRenderResolutionReadout = (
      resolved: ResolvedRenderResolution,
      applied: number,
    ): void => {
      (window as unknown as { __tsrRenderResolution?: unknown }).__tsrRenderResolution = {
        ...resolved,
        applied,
        /** QA — render at an explicit ratio without reload (A/B perf probe). */
        set: (r: number): number => {
          const a = applyRenderRatio(viewer, r);
          // read the CURRENT display density (not the captured one) so an A/B
          // probe after a display move reports an honest devicePixelRatio.
          const dpr =
            typeof window !== 'undefined' && Number.isFinite(window.devicePixelRatio)
              ? window.devicePixelRatio
              : resolved.devicePixelRatio;
          writeRenderResolutionReadout({ ratio: a, source: 'forced', devicePixelRatio: dpr }, a);
          return a;
        },
        /** QA — re-resolve from URL/localStorage/DPR and apply. */
        reapply: (): void => applyResolution(),
      };
    };
    const applyResolution = (): void => {
      const resolved = resolveRenderResolution();
      const applied = applyRenderRatio(viewer, resolved.ratio);
      writeRenderResolutionReadout(resolved, applied);
    };
    applyResolution();
    const stopDprWatch = watchDevicePixelRatio(applyResolution);
    viewer.scene.globe.enableLighting = false;
    // Live-aircraft smooth motion (D2 §3.3, 3D wave) is clock-driven: Cesium
    // interpolates each SampledPositionProperty along viewer.clock. We pin the
    // clock to the wall clock every frame (see scene.preUpdate below) so display
    // time matches the JulianDate.now() stamps the samples are laid against —
    // robust to frame throttling instead of relying on multiplier accrual.
    viewer.clock.shouldAnimate = true;
    viewer.clock.clockRange = Cesium.ClockRange.UNBOUNDED;
    // First-paint framing precedence (#138/#149):
    //  1. #at= shareable view hash → exact point (existing behaviour).
    //  2. an entity deep-link (#cam/#fire/...) → neutral Canada frame; App's
    //     deep-link effect flies to the entity right after mount.
    //  3. no deep-link → the operator's saved home view (#149), else the
    //     all-Canada country-zoom default (#138).
    const fromHash = parseInitialCameraFromHash();
    const { west, south, east, north } = CANADA_VIEW_BOUNDS;
    const canadaDefault = Cesium.Rectangle.fromDegrees(west, south, east, north);
    let initialDestination: Cesium.Cartesian3 | Cesium.Rectangle;
    if (fromHash) {
      initialDestination = Cesium.Cartesian3.fromDegrees(
        fromHash.lon,
        fromHash.lat,
        fromHash.heightM,
      );
    } else if (hasEntityDeepLink()) {
      initialDestination = canadaDefault;
    } else {
      const home = loadHomeView();
      initialDestination = home
        ? Cesium.Cartesian3.fromDegrees(home.lon, home.lat, home.heightM)
        : canadaDefault;
    }
    viewer.camera.flyTo({ destination: initialDestination, duration: 0 });

    // Expose a flight controller so App can dive the camera to a clicked marker.
    propsRef.current.flyControllerRef.current = createFlyController(viewer);

    // #255 red-yield rule (Law 6 amendment): while any unacked URGENT-RED is in
    // view, ALL green animation goes still — red owns motion exclusively. The
    // per-frame green callbacks read a cached verdict recomputed at most every
    // GREEN_YIELD_RECHECK_MS from the urgent registry (the three URGENT-RED
    // anomaly layers report their unacked markers there on each render, so an
    // ack/clear releases the yield on the same poll that recolours the dot).
    const GREEN_YIELD_RECHECK_MS = 250;
    let greenYieldCheckedAt = -Infinity;
    let greenYielding = false;
    const greenMotionAllowedNow = (): boolean => {
      const now = performance.now();
      if (now - greenYieldCheckedAt >= GREEN_YIELD_RECHECK_MS) {
        greenYieldCheckedAt = now;
        const rect = viewer.camera.computeViewRectangle();
        greenYielding = rect
          ? urgentRedRegistry.anyInView({
              ...rectangleToBbox(rect),
              eyeHeightM: viewer.camera.positionCartographic.height,
            })
          : false;
      }
      return !greenYielding;
    };

    // Two independent, frame-evaluated pulses (continuous render is on by
    // default). They compose: a live camera's inner dot breathes via
    // `liveInnerPulse`, and if that camera also has an open pop-out window an
    // extra `popoutRing` halo pulses behind it via `popoutRingPulse` — the
    // pop-out pulse never overwrites the live pulse because they live on
    // separate entities (Phase V.1 Part 2).
    //
    // Live inner dot: the Law-6 green breathe — calm ±10% scale on a 2 s
    // period (family rule, #255 amendment), STILL while red owns motion.
    const liveInnerPulse = new Cesium.CallbackProperty(() => {
      // WALL profile (#132) scales the breathing dot; operator COP scale = 1.
      const s = propsRef.current.markerScale ?? 1;
      return greenBreatheSize(
        LIVE_DOT_BASE_SIZE * s,
        performance.now() / 1000,
        !greenMotionAllowedNow(),
      );
    }, false);
    // Law 6 v2 (#267): the green GLOW — a soft halo billboard behind every
    // breathing green dot, swelling in phase with the breathe (one heartbeat,
    // two instruments). ONE shared radial sprite (glowTexture.ts → a single
    // atlas texture) tinted live-green; size + alpha ride shared callbacks for
    // all live singles (clusters get per-cluster size callbacks — their base
    // size varies with the count). Red-yield freezes the halo at its rest
    // register — present but still, zero animated pixels.
    const glowSprite = glowTextureDataUrl();
    const singleGlowPulse = new Cesium.CallbackProperty(() => {
      const s = propsRef.current.markerScale ?? 1;
      return greenGlowSize(
        LIVE_DOT_BASE_SIZE * s,
        performance.now() / 1000,
        !greenMotionAllowedNow(),
      );
    }, false);
    const GLOW_GREEN = Cesium.Color.fromCssColorString(COVERAGE_LIVE);
    const glowColorPulse = new Cesium.CallbackProperty(
      (_time: unknown, result?: Cesium.Color) =>
        GLOW_GREEN.withAlpha(
          greenGlowAlpha(performance.now() / 1000, !greenMotionAllowedNow()),
          result,
        ),
      false,
    );
    // Pop-out halo: a larger 18→26 px ring on a 1.1 s cosine period (reuses
    // Phase V's pop-out pulse cadence, now drawn as a separate halo entity).
    const POPOUT_PERIOD_MS = 1100;
    const POPOUT_RING_BASE = 18;
    const popoutRingSize = (): number => {
      const phase = (performance.now() % POPOUT_PERIOD_MS) / POPOUT_PERIOD_MS;
      return POPOUT_RING_BASE + 8 * (0.5 - 0.5 * Math.cos(phase * 2 * Math.PI));
    };
    const popoutRingPulse = new Cesium.CallbackProperty(popoutRingSize, false);
    // A live camera's pop-out halo is green-family motion, so it yields with
    // the rest of the greens (rests at its base size); snapshot-blue halos are
    // outside the amendment's scope and keep the plain pulse.
    const popoutRingPulseGreen = new Cesium.CallbackProperty(
      () => (greenMotionAllowedNow() ? popoutRingSize() : POPOUT_RING_BASE),
      false,
    );

    // Altitude-dependent cone opacity (Phase V.2 §5). Wide views read the cone
    // colour clearly; close in, the cone goes near-transparent so the satellite
    // imagery shows through. Linear fade across the mid band; flat at the ends.
    //   eye > 50 km        → 0.5  (full colour)
    //   5 km – 50 km       → linear 0.25 → 0.5
    //   eye < 5 km         → 0.15 (very transparent)
    const CONE_ALPHA_FAR = 0.5;
    const CONE_ALPHA_MID = 0.25;
    const CONE_ALPHA_NEAR = 0.15;
    const CONE_ALT_FAR_M = 50000;
    const CONE_ALT_NEAR_M = 5000;
    const coneAltitudeAlpha = (): number => {
      const h = viewer.camera.positionCartographic.height;
      if (h >= CONE_ALT_FAR_M) return CONE_ALPHA_FAR;
      if (h <= CONE_ALT_NEAR_M) return CONE_ALPHA_NEAR;
      const t = (h - CONE_ALT_NEAR_M) / (CONE_ALT_FAR_M - CONE_ALT_NEAR_M);
      return CONE_ALPHA_MID + t * (CONE_ALPHA_FAR - CONE_ALPHA_MID);
    };
    // The cone the cursor is currently over (Phase V.2 §2). Hovering bumps that
    // cone's alpha by 20% as a "clickable" affordance. Updated by the MOUSE_MOVE
    // handler; read each frame by every cone's material CallbackProperty.
    let hoveredConeId: string | null = null;

    const cameras = new Map<string, CameraEntry>();
    // #195 — camera cluster markers, keyed by cluster id. A cluster collapses 2+
    // nearby cameras into one ambient count marker; single-camera "clusters" render
    // as a normal camera marker via `cameras` above (not stored here).
    const cameraClusters = new Map<string, { entity: Cesium.Entity; cluster: CameraCluster }>();
    // Last camera features fetched for the current view — kept so a pure zoom (no
    // new fetch) can re-cluster against the new merge radius without a round-trip.
    let lastCameraFeatures: CameraFeature[] = [];
    // #255 — size of the tier-visible subset the last rebuild rendered (the
    // layer count badge truth; the in-flight/error count paths reuse it).
    let lastTierVisibleCount = 0;
    // Pop-out halo entities, keyed by camera id; spawned/removed by applyPulse.
    const popoutRings = new Map<string, Cesium.Entity>();
    const sensors = new Map<string, SensorEntry>();
    const assets = new Map<string, { feature: ResponseAssetFeature; entity: Cesium.Entity }>();
    const aircraft = new Map<string, AircraftEntry>();
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    let aircraftTimer: ReturnType<typeof setInterval> | null = null;
    let anomalyTimer: ReturnType<typeof setInterval> | null = null;
    // WALL profile (#132): an unattended wall has a fixed view, so the bbox-driven
    // camera/fire/CAP refresh never fires. This optional timer re-polls the whole
    // visible Tier-0/1 set so the wall stays live. Unset in the operator COP.
    let wallTimer: ReturnType<typeof setInterval> | null = null;
    let inflightCameras: AbortController | null = null;
    let inflightSensors: AbortController | null = null;
    let inflightAssets: AbortController | null = null;
    let inflightAircraft: AbortController | null = null;
    // Stage 5 context layers — decorative provincial boundaries (async fetch)
    // and curated city labels. Disposed in teardown before viewer.destroy().
    let boundaryLayer: BoundaryLayerHandle | null = null;
    let cityLabels: CityLabelsHandle | null = null;
    // #410 act-over-ambient z-order (greenMotion.orderAmbientFirst, Icon Council
    // Ruling 1/3): the ACT-register layers — ActiveFires, HighRiskHotspots, CAP /
    // Extreme Weather, and the Smoke/Ice/Flood anomalies — plus the Phase 5a
    // OC-fire bridge install LAST (after every ambient-register layer) so they
    // paint ABOVE the ambient register and an urgent red is never occluded by a
    // community/asset marker. See the ACT-REGISTER install block below.
    // L.2.2 — Drone Operator Coverage (§3B.6): base marker + coverage area,
    // highlighting reachability against the selected incident. Phase 6 legibility:
    // installed FIRST among the ambient layers so the faint coverage WASH (α0.07)
    // is seated at the BOTTOM of the ambient z-stack — communities / road-closures /
    // staging / assets paint OVER it, never the reverse. The act-register installs
    // LAST (#410) and still wins the top, and the #348 click arbitration is by pick
    // logic (preferNonCoverageFillPick), not z-order, so it is unaffected.
    const droneCoverageLayer: DroneCoverageLayerHandle = installDroneCoverageLayer(viewer, {
      onCount: (n) => propsRef.current.onDroneCoverageCountChange(n),
      getVisible: () => propsRef.current.showDroneCoverage,
      getSelectedIncident: () => propsRef.current.selectedIncidentPoint ?? null,
    });
    // E.W.0.5 — Raw Hot Spots (pre-fusion satellite thermal): ambient source-
    // coloured dots, its own CustomDataSource, self-gated to regional+ zoom.
    const rawHotspotsLayer: RawHotspotsLayerHandle = installRawHotspotsLayer(viewer, {
      onCount: (n) => propsRef.current.onRawHotspotCountChange?.(n),
      getVisible: () => propsRef.current.showRawHotspots,
    });
    // #370 — Fusion Candidates (below-urgent L1/L2 fused hotspots): the demoted
    // mass off the urgent surface, its own CustomDataSource, self-gated to
    // regional+ zoom. Default OFF.
    const fusionCandidatesLayer: FusionCandidatesLayerHandle = installFusionCandidatesLayer(
      viewer,
      {
        getVisible: () => propsRef.current.showFusionCandidates,
      },
    );
    // E.W.5 — Camera Smoke Detections (pre-fusion camera-CV smoke): ambient amber
    // camera-glyph dots, its own CustomDataSource, self-gated to regional+ zoom.
    const cameraSmokeLayer: CameraSmokeLayerHandle = installCameraSmokeLayer(viewer, {
      onCount: (n) => propsRef.current.onCameraSmokeCountChange?.(n),
      getVisible: () => propsRef.current.showCameraSmoke,
    });
    // Power Outages (Ice Storm Risk): ambient status-coloured dots sized by
    // customers-affected + ground-clamped service-area polygons. Its own
    // CustomDataSource, self-gated to regional+ zoom.
    const powerOutagesLayer: PowerOutagesLayerHandle = installPowerOutagesLayer(viewer, {
      onCount: (n) => propsRef.current.onUtilityOutageCountChange?.(n),
      getVisible: () => propsRef.current.showPowerOutages,
    });
    // Road Closures (Ice Storm Risk): ambient register-coloured dots anchoring a
    // ground-clamped affected-segment line. Its own CustomDataSource, self-gated to
    // regional+ zoom.
    const roadClosuresLayer: RoadClosuresLayerHandle = installRoadClosuresLayer(viewer, {
      onCount: (n) => propsRef.current.onRoadClosureCountChange?.(n),
      getVisible: () => propsRef.current.showRoadClosures,
      getAllowRestrictions: () => propsRef.current.roadClosuresRestrictions,
    });
    // Estimated Comms Degradation (#109) — the fusion-derived comms_degradation_zones
    // render: a faint ground-clamped translucent indigo ESTIMATE polygon + centroid
    // label. Its own CustomDataSource, self-gated to regional+ zoom.
    const commsDegradationZonesLayer: CommsDegradationZonesLayerHandle =
      installCommsDegradationZonesLayer(viewer, {
        getVisible: () => propsRef.current.showCommsDegradationZones,
      });
    // Public-ArcGIS category renders (#213) — the generalized config-driven family:
    // evacuation (#204) + fire bans + drought + boil-water + SoLE + cyclones. The
    // manager mounts one GeoJsonDataSource per category, each self-gated to regional+
    // zoom, and fans MapView's refresh/visibility/pick calls out across them.
    const arcgisLayers: ArcgisCategoryLayersHandle = installArcgisCategoryLayers(viewer, {
      getVisibility: (category) => propsRef.current.arcgisCategoryVisibility[category] ?? false,
    });
    // Hydrometric Gauges (Flood Risk): ambient WSC gauge dots coloured by the
    // rate-of-rise band. Its own CustomDataSource, self-gated to regional+ zoom.
    const hydrometricGaugesLayer: HydrometricGaugesLayerHandle = installHydrometricGaugesLayer(
      viewer,
      {
        onCount: (n) => propsRef.current.onHydrometricGaugeCountChange?.(n),
        getVisible: () => propsRef.current.showHydrometricGauges,
      },
    );
    // 3D Flood Inundation (#177 Cesium-wow): World Terrain + OSM Buildings + a
    // settable translucent water surface over the bounded Kashechewan demo region.
    // Driven by props (on/off) + the floodInundationControllerRef (level + fly).
    const floodInundationLayer: FloodInundationLayerHandle = installFloodInundationLayer(viewer);
    // Camera Line-of-Sight Viewshed (#178 Cesium-wow): a modelled terrain LOS
    // footprint for the SELECTED camera (World Terrain + draped visible-area polygon).
    // Driven by props: setEnabled on the toggle, setCamera on selection change.
    const cameraViewshedLayer: CameraViewshedLayerHandle = installCameraViewshedLayer(viewer, {
      onComputed: (info) =>
        console.info(
          `[viewshed] radius=${info.radiusM}m rays=${info.rays} samples=${info.samples} recompute=${info.ms.toFixed(0)}ms`,
        ),
    });
    // L.2.2 — marine vessels (R.1d render): the marine half of Aircraft & Marine,
    // gated on the same showLiveAircraft toggle as the inline aircraft render.
    const marineLayer: MarineVesselsLayerHandle = installMarineVesselsLayer(viewer, {
      onCount: (n) => propsRef.current.onVesselCountChange(n),
      getVisible: () => propsRef.current.showLiveAircraft,
    });
    // W.2 — Vulnerable Communities (Flood Risk + Ice Storm Risk groups): ambient
    // house-glyph markers coloured by wellness status, gated on `showCommunities`
    // (the OR of the two group toggles). Status + headcount chip come from App's
    // detail cache via getStatus/getBadge; onInView lets App lazily fill it.
    const communitiesLayer: CommunitiesLayerHandle = installCommunitiesLayer(viewer, {
      onCount: (n) => propsRef.current.onCommunityCountChange?.(n),
      getVisible: () => propsRef.current.showCommunities,
      getStatus: (id) => propsRef.current.communityStatuses[id]?.status ?? null,
      getBadge: (id) => propsRef.current.communityStatuses[id]?.badge ?? null,
      onInView: (ids) => propsRef.current.onCommunitiesInView?.(ids),
      getVulnerabilityShading: () => propsRef.current.communityVulnerabilityShading,
    });
    // R.4.2 — Staging Areas (§2.5/§6.4): ambient depot glyphs for open staging
    // areas in view, gated on `showStagingAreas`.
    const stagingAreasLayer: StagingAreasLayerHandle = installStagingAreasLayer(viewer, {
      onCount: (n) => propsRef.current.onStagingAreaCountChange?.(n),
      getVisible: () => propsRef.current.showStagingAreas,
    });
    // R5 Slice 3 (#507) — Area Command (§4.6): AMBIENT oversight markers, installed
    // here among the ambient layers (BEFORE the ACT-REGISTER block below, #410) so an
    // urgent child incident is never occluded by its parent. Fetch/poll is NOT gated
    // on visibility — the live count feeds the layer-picker row while the layer is off.
    const areaCommandLayer: AreaCommandLayerHandle = installAreaCommandLayer(viewer, {
      onCount: (n, areaCommands) => propsRef.current.onAreaCommandCountChange?.(n, areaCommands),
      getVisible: () => propsRef.current.showAreaCommand,
    });
    // Task #64 — Sectors (§4.3): incident-scoped dashed polygons, set via
    // setIncident in applyVisibility. Ambient register, never pulses.
    const sectorsLayer: SectorsLayerHandle = installSectorsLayer(viewer, {
      onCount: (n) => propsRef.current.onSectorCountChange?.(n),
      getVisible: () => propsRef.current.showSectors,
    });
    // #445-(3) — operator-declared evac orders: a PARALLEL violet layer to the
    // external GeoBC evac (arcgis 'evacuation'). Default OFF, regional+ gated.
    const operatorEvacLayer: OperatorEvacOrderLayerHandle = installOperatorEvacuationOrderLayer(
      viewer,
      { getVisible: () => propsRef.current.showOperatorEvacOrders },
    );

    // ─────────────────── ACT-REGISTER layers — install LAST (#410) ───────────────
    // greenMotion.orderAmbientFirst / Icon Council Ruling 1/3 + design-council
    // §3B.8: every marker disables the depth test, so build order = z-order (the
    // dataSource added LAST paints on top). The urgent INCIDENT layers install AFTER
    // every ambient-register context layer above, so an URGENT-RED dot is NEVER
    // occluded by a community / asset / sector marker and always wins the top-most
    // pick. Act-INTERNAL order is preserved: ActiveFires before HighRiskHotspots
    // (the Phase 5a OC-fire co-location suppression reads the OC-FUL set when the
    // hotspot layer renders), then CAP / Extreme Weather, then the Smoke/Ice/Flood
    // anomalies. (Fanning colliding ACT markers apart is the separate Phase 6b SPREAD.)
    //
    // Phase 5a (council §A "OC-fire unclickable") — late-bound bridge: the fires
    // layer notifies when its OC-FUL fire set changes, and the hotspot layer
    // re-evaluates which dots to suppress. Fires installs FIRST (so highRiskLayer
    // isn't defined yet), so route the notification through this holder and bind
    // it once both handles exist.
    const ocFulBridge: { reapply?: () => void } = {};
    // Fire Risk layer (E.2.1) — its own GeoJsonDataSource + 300ms debounce.
    const firesLayer: ActiveFiresLayerHandle = installActiveFiresLayer(viewer, {
      onCount: (n) => propsRef.current.onFireCountChange(n),
      getVisible: () => propsRef.current.showFireRisk,
      // L.1.2 — `undefined` = all stages (All Reported Wildfires); ['OC'] = the
      // L.0 out-of-control-only layer. Read at fetch time.
      getStages: () => propsRef.current.fireStages,
      // #607/#621 — the Default OC Layer 4 passes NO response_type filter (undefined =
      // every OC posture; the LOD governs visibility). Read at fetch time.
      getResponseTypes: () => propsRef.current.fireResponseTypes,
      getScale: () => propsRef.current.markerScale ?? 1,
      onOcFulFiresChanged: () => ocFulBridge.reapply?.(),
    });
    // High-Risk Hotspots layer (E.2.3, L.0 layer 3) — its own CustomDataSource.
    const highRiskLayer: HighRiskHotspotsLayerHandle = installHighRiskHotspotsLayer(viewer, {
      onCount: (n) => propsRef.current.onHighRiskCountChange(n),
      getVisible: () => propsRef.current.showHighRiskHotspots,
      getScale: () => propsRef.current.markerScale ?? 1,
      getLiftAckedGate: () => propsRef.current.liftAckedZoomGates === true,
      // Phase 5a — read the current OC-FUL fires to suppress a redundant co-located
      // hotspot dot (the §A "one clickable fire, not a stack" fix).
      getOcFulFires: () => firesLayer.ocFulFires(),
    });
    // Phase 5a — bind the bridge now that both handles exist: an OC-FUL-set change
    // re-runs the hotspot render so a newly-co-located dot is suppressed (and a
    // departed OC-FUL fire restores its dot). No-op before the hotspot layer's first
    // fetch.
    ocFulBridge.reapply = () => highRiskLayer.reapplySuppression();
    // Extreme Weather Warnings (EWW) — the CAP-polygon half of the multi-source
    // Layer #2 surface. Mounts alongside highRiskLayer; its own GeoJsonDataSource.
    const extremeWeatherLayer: ExtremeWeatherWarningsLayerHandle =
      installExtremeWeatherWarningsLayer(viewer, {
        getVisible: () => propsRef.current.showExtremeWeatherWarnings,
        getLiftAckedGate: () => propsRef.current.liftAckedZoomGates === true,
      });
    // L.2.2 — smoke_detected anomalies (§6.2): the third Anomaly-Queue source,
    // mounted alongside highRisk + CAP, gated on the same Anomaly Queue toggle.
    const smokeAnomaliesLayer: SmokeAnomaliesLayerHandle = installSmokeAnomaliesLayer(viewer, {
      onCount: (n) => propsRef.current.onSmokeAnomalyCountChange(n),
      getVisible: () => propsRef.current.showHighRiskHotspots,
      getLiftAckedGate: () => propsRef.current.liftAckedZoomGates === true,
    });
    // Ice Storm Fusion Anomaly (§4.5): the act-register outage-cluster + ice-warning
    // signal — a fourth Anomaly-Queue source on the same Anomaly Queue toggle.
    const iceAnomaliesLayer: IceStormAnomaliesLayerHandle = installIceStormAnomaliesLayer(viewer, {
      onCount: (n) => propsRef.current.onIceAnomalyCountChange(n),
      getVisible: () => propsRef.current.showHighRiskHotspots,
      getScale: () => propsRef.current.markerScale ?? 1,
      getLiftAckedGate: () => propsRef.current.liftAckedZoomGates === true,
    });
    // Flood Fusion Anomaly (§4.4): the act-register gauge rate-of-rise + community-in-
    // reach signal — a fifth Anomaly-Queue source on the same Anomaly Queue toggle.
    const floodAnomaliesLayer: FloodAnomaliesLayerHandle = installFloodAnomaliesLayer(viewer, {
      onCount: (n) => propsRef.current.onFloodAnomalyCountChange?.(n),
      getVisible: () => propsRef.current.showHighRiskHotspots,
      getScale: () => propsRef.current.markerScale ?? 1,
      getLiftAckedGate: () => propsRef.current.liftAckedZoomGates === true,
    });

    // Task #64 — the polygon draw tool (its own ScreenSpaceEventHandler + draw
    // CustomDataSource). The main click handler short-circuits while it's active.
    const sectorDraw: SectorDrawToolHandle = installSectorDrawTool(viewer, {
      onComplete: (ring) => propsRef.current.onSectorDrawn(ring),
      onStateChange: () => applyVisibilityRef.current(),
    });
    // #445-(3) — the evacuation-area draw tool (its own ScreenSpaceEventHandler +
    // draw CustomDataSource). The main click handler short-circuits while active.
    const evacDraw: EvacuationDrawToolHandle = installEvacuationDrawTool(viewer, {
      onComplete: (ring) => propsRef.current.onEvacOrderDrawn(ring),
      onStateChange: () => applyVisibilityRef.current(),
    });
    // W.2 editor — the place-a-point tool (its own ScreenSpaceEventHandler + draw
    // CustomDataSource). The main click handler short-circuits while it's active.
    const communityPlace: CommunityPlaceToolHandle = installCommunityPlaceTool(viewer, {
      onPlaced: (lonLat) => propsRef.current.onCommunityPlaced(lonLat),
      onStateChange: (active) => propsRef.current.onCommunityPlaceStateChange?.(active),
    });
    // FWI heatmap (E.2.2) — a WMS imagery overlay (not entities/dataSource).
    // Starts hidden; the Weather-group toggle drives its visibility.
    const fwiLayer: FwiHeatmapLayerHandle = installFwiHeatmapLayer(viewer);
    // #212 — official government WMS raster overlays (ECCC hurricane track + NRCan
    // EGS flood extent). Client-side imagery layers, all start hidden; the group
    // toggles drive visibility through the wmsOverlayVisibility map.
    const wmsOverlays: WmsOverlaysHandle = installWmsOverlays(
      viewer,
      // The WALL appends its radar overlay (#272 Ruling 1.4) — never a COP row.
      [...WMS_OVERLAYS, ...(propsRef.current.extraWmsOverlays ?? [])],
      {
        getVisibility: (key) => propsRef.current.wmsOverlayVisibility[key] ?? false,
        onRefresh: (key, atMs) => propsRef.current.onWmsOverlayRefresh?.(key, atMs),
      },
    );
    let destroyed = false;
    // Exposed for verification (headless preview throttles rAF, so motion is
    // checked by reading these via preview_eval rather than by screenshot).
    (window as unknown as { __tsrViewer?: unknown }).__tsrViewer = undefined;
    (window as unknown as { __tsrAircraft?: unknown }).__tsrAircraft = aircraft;

    // Layer toggle only. Per-camera zoom culling is handled by each entity's
    // distanceDisplayCondition (set at creation), so cones and markers fade in
    // together as the camera descends — no blanket count threshold.
    const applyVisibility = (): void => {
      const { showCameras, showSensors } = propsRef.current;
      for (const c of cameras.values()) {
        c.marker.show = showCameras;
        c.footprint.show = showCameras;
        if (c.glow) c.glow.show = showCameras;
        if (c.glyph) c.glyph.show = showCameras;
      }
      // #195 — cluster count markers ride the same Cameras toggle.
      for (const { entity } of cameraClusters.values()) entity.show = showCameras;
      for (const ring of popoutRings.values()) {
        ring.show = propsRef.current.showCameras;
      }
      for (const s of sensors.values()) {
        s.marker.show = showSensors;
      }
      // Task #64 — sectors: incident-scoped (setIncident is idempotent when the
      // id is unchanged) + toggle-gated visibility.
      sectorsLayer.setIncident(propsRef.current.sectorIncidentId);
      sectorsLayer.setVisible(propsRef.current.showSectors);
      // #445-(3) — operator-declared evac orders (toggle-gated, regional+).
      operatorEvacLayer.setVisible(propsRef.current.showOperatorEvacOrders);
    };

    // #428 — the incident-scoped layers that `applyVisibility` re-applies ONLY on a
    // camera/sensor rebuild or a draw-tool change: the sector overlay's incident
    // (setIncident is the SOLE fetch trigger — sectors have no bbox refresh fallback)
    // and the operator-evac layer's visibility. NONE of applyVisibility's triggers
    // fire on the read-only WALL (cameras/sensors are off, no drawing), so a LIVE
    // escalation — a `sectorIncidentId` / `showOperatorEvacOrders` prop change on an
    // ALREADY-mounted wall (not a remount) — would otherwise never surface the
    // escalated incident's sectors. A dedicated effect keyed on those props drives
    // this (mirrors the per-toggle apply effects). setIncident triggers its own
    // fetch; operator-evac's data is (re)fetched by the wall refresh loop at the
    // incident bbox once setVisible is true.
    const applyIncidentScopedLayers = (): void => {
      sectorsLayer.setIncident(propsRef.current.sectorIncidentId);
      operatorEvacLayer.setVisible(propsRef.current.showOperatorEvacOrders);
    };

    // Reconcile pop-out halo entities against the set of cameras with an open
    // pop-out window. A halo is an extra pulsing ring drawn behind the marker;
    // it composes with (does not replace) a live camera's inner pulse.
    const applyPulse = (): void => {
      const pulsing = propsRef.current.pulsingCameraIds;
      for (const [id, ring] of popoutRings) {
        if (!pulsing.has(id) || !cameras.has(id)) {
          viewer.entities.remove(ring);
          popoutRings.delete(id);
        }
      }
      for (const id of pulsing) {
        if (popoutRings.has(id)) continue;
        const c = cameras.get(id);
        if (!c) continue;
        const [lon, lat] = c.feature.geometry.coordinates;
        const color = coverageColorFor(c.feature.properties.stream_type);
        const ring = viewer.entities.add({
          id: `popoutring:${id}`,
          position: Cesium.Cartesian3.fromDegrees(lon, lat),
          point: {
            // #255 — a live camera's halo is green-family motion: it yields
            // (rests at base size) while an unacked urgent-red is in view.
            pixelSize: isLiveCamera(c.feature) ? popoutRingPulseGreen : popoutRingPulse,
            color: Cesium.Color.fromCssColorString(color).withAlpha(0.35),
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, c.far),
            show: propsRef.current.showCameras,
          },
        });
        popoutRings.set(id, ring);
      }
    };

    // #195 — ground distance (m) under the cluster pixel radius at the current
    // zoom. Cameras closer than this on screen merge into one count marker; the
    // radius shrinks as the operator zooms in so clusters dissolve on the way
    // down. Floored so a tight site stays one cluster even at street zoom.
    const currentClusterRadiusM = (): number => {
      const height = viewer.camera.positionCartographic.height;
      const canvasH = viewer.canvas.clientHeight || viewer.canvas.height || 800;
      const frustum = viewer.camera.frustum as Cesium.PerspectiveFrustum;
      const fovy =
        typeof frustum.fovy === 'number' && Number.isFinite(frustum.fovy)
          ? frustum.fovy
          : Math.PI / 3;
      const mpp = metersPerPixel(height, canvasH, fovy);
      return Math.max(MIN_CLUSTER_RADIUS_M, mpp * CLUSTER_PIXEL_RADIUS);
    };

    // Build the single-camera marker stack (dot + cone/disc + optional glow +
    // video glyph). Extracted from the render loop so a one-member cluster renders
    // a full camera while a multi-member cluster renders one count marker instead.
    const renderSingleCamera = (f: CameraFeature, camScale: number): void => {
      const [lon, lat] = f.geometry.coordinates;
      const baseColor = coverageColorFor(f.properties.stream_type);
      const position = Cesium.Cartesian3.fromDegrees(lon, lat);
      // Per-camera zoom tiering (Stage 5 §5.5). `far` is the max eye distance
      // at which this camera draws: live + smoke-priority cams resolve to a
      // country-wide far (the coverage view), rural snapshots to regional,
      // urban-dense snapshots keep V.1 city/street tiering. Marker, cone,
      // glow + glyph all share this `far` so they bloom in together.
      const far = cameraVisibilityFar({
        streamType: f.properties.stream_type,
        smokePriority: f.properties.smoke_priority,
        lat,
        lon,
        rangeM: f.properties.range_m,
      });
      const ddc = new Cesium.DistanceDisplayCondition(0, far);
      const live = isLiveCamera(f);
      const unhealthy = isUrlUnhealthy(f);

      // Live cams get the Law-6 v2 GLOW halo behind the inner dot (#267): a
      // soft radial sprite swelling in phase with the breathe, frozen at its
      // rest register while red owns motion. Replaces the pre-v2 static
      // 18px point. Snapshot cams are a single flat dot — never haloed.
      // (No outline on either — Phase V.1 Part 2. The sprite-less fallback
      // when canvas is unavailable simply skips the halo, like the glyphs.)
      const glow =
        live && glowSprite
          ? viewer.entities.add({
              id: `glow:${f.id}`,
              position,
              billboard: {
                image: glowSprite,
                width: singleGlowPulse,
                height: singleGlowPulse,
                color: glowColorPulse,
                verticalOrigin: Cesium.VerticalOrigin.CENTER,
                disableDepthTestDistance: Number.POSITIVE_INFINITY,
                distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, far),
              },
            })
          : null;
      const marker = viewer.entities.add({
        id: `camera:${f.id}`,
        position,
        point: {
          // Live inner dot breathes via the shared pulse; snapshot is static.
          pixelSize: live
            ? liveInnerPulse
            : new Cesium.ConstantProperty(SNAPSHOT_DOT_SIZE * camScale),
          // Broken-source markers are dimmed (alpha 0.4) so they read as
          // degraded without disappearing.
          color: unhealthy
            ? Cesium.Color.fromCssColorString(baseColor).withAlpha(UNHEALTHY_ALPHA)
            : Cesium.Color.fromCssColorString(baseColor),
          outlineColor: Cesium.Color.fromCssColorString(UNHEALTHY_OUTLINE),
          outlineWidth: unhealthy ? 1.5 : 0,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, far),
        },
      });
      const knownHeading = f.properties.heading_known;
      const cappedRange = Math.min(f.properties.range_m, MAX_FOOTPRINT_RANGE_M);
      // PTZ snapshot cams rotate 360° on a cadence — their stored heading is a
      // momentary aim, so a directional cone misleads. Render a full
      // omnidirectional disc instead (Phase Q.2.5.3), at the cam's
      // authoritative range_m (fire-tower ranges of 3.5–10 km are deliberate,
      // not the AI over-estimate the cone-path cap guards against). Live/hls/
      // youtube_live and fixed (is_ptz=false) cams keep cone-or-coverage-disc.
      const isPtzSnapshot = f.properties.is_ptz && f.properties.stream_type === 'snapshot';
      const renderDisc = isPtzSnapshot || !knownHeading;
      // Three opacity registers (Task #48b): directional cone > heading-unknown
      // disc > PTZ 360° disc. A PTZ snapshot is always a disc; a non-PTZ cam is
      // a cone when its heading is known, else an ambient unknown-heading disc.
      const footprintShape: FootprintShape = isPtzSnapshot
        ? 'ptzDisc'
        : knownHeading
          ? 'cone'
          : 'unknownDisc';
      const polygon = renderDisc
        ? cameraCoveragePolygon({
            lat,
            lon,
            // PTZ 360° discs keep their authoritative range (deliberate
            // wide-area fire-tower coverage). A heading-unknown disc is the
            // ambient register and carries no aim, so it gets the tighter
            // QA-7 ceiling — no single bad row can paint a city-sized disc.
            rangeM: isPtzSnapshot ? f.properties.range_m : clampUnknownDiscRange(cappedRange),
            segments: isPtzSnapshot ? 48 : 16,
          })
        : cameraConePolygon({
            lat,
            lon,
            headingDeg: f.properties.heading_deg,
            fovDeg: f.properties.fov_deg,
            rangeM: cappedRange,
            segments: 16,
          });
      const ring = polygon.coordinates[0]!.flatMap(([x, y]) => [x, y]);
      // Cone/disc use the same colour as the marker — shape (wedge vs
      // full disc) carries the known/unknown distinction. Alpha is driven per
      // frame by camera altitude (§5) with a +20% hover bump (§2); the base
      // RGB is fixed per source family.
      const coneBaseColor = Cesium.Color.fromCssColorString(baseColor);
      const coneFillId = f.id;
      const coneMaterial = new Cesium.ColorMaterialProperty(
        new Cesium.CallbackProperty(() => {
          // Shape register + broken-camera dim + hover bump, all folded into
          // one pure helper (Task #48). PTZ discs ignore the altitude ramp;
          // cones/unknown-discs ride it; unhealthy cams dim in lockstep with
          // the marker so dead cameras never paint bright coverage.
          return coneBaseColor.withAlpha(
            footprintAlpha({
              shape: footprintShape,
              coneRampAlpha: coneAltitudeAlpha(),
              unhealthy,
              hovered: hoveredConeId === coneFillId,
            }),
          );
        }, false),
      );
      const footprint = viewer.entities.add({
        id: `cone:${f.id}`,
        polygon: {
          hierarchy: Cesium.Cartesian3.fromDegreesArray(ring),
          material: coneMaterial,
          // Omit `height` → polygon clamps to terrain.
          // Outlines aren't supported on clamped polygons; the marker carries
          // the visual emphasis.
          outline: false,
          distanceDisplayCondition: ddc,
        },
      });
      // Video-glyph overlay so the marker reads as a camera (Stage 4). White
      // glyph centred on the dot; broken sources dim to match the dot's alpha.
      // Lives on its own entity (id `camglyph:`) so it never disturbs the V.1
      // live pulse / glow or the V.2.2 broken treatment on the dot beneath.
      const glyphUrl = renderLucideToDataUrl('video', {
        color: CAM_GLYPH_COLOR,
        size: live ? CAM_GLYPH_SIZE_LIVE : CAM_GLYPH_SIZE_SNAPSHOT,
      });
      const glyph = glyphUrl
        ? viewer.entities.add({
            id: `camglyph:${f.id}`,
            position,
            billboard: {
              image: glyphUrl,
              width: (live ? CAM_GLYPH_SIZE_LIVE : CAM_GLYPH_SIZE_SNAPSHOT) * camScale,
              height: (live ? CAM_GLYPH_SIZE_LIVE : CAM_GLYPH_SIZE_SNAPSHOT) * camScale,
              color: unhealthy ? Cesium.Color.WHITE.withAlpha(UNHEALTHY_ALPHA) : Cesium.Color.WHITE,
              disableDepthTestDistance: Number.POSITIVE_INFINITY,
              verticalOrigin: Cesium.VerticalOrigin.CENTER,
              distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, far),
            },
          })
        : null;
      cameras.set(f.id, { feature: f, marker, footprint, glow, glyph, far });
    };

    // #195/#255/#267 — render a multi-camera cluster as one count marker. The
    // treatment follows the cluster's family (Law 6 amendments, founder ruling
    // ii + v2): a live cluster (all live-family, ≥1 currently up) is whole-dot
    // LIVE-GREEN with the governed green breathe AND the v2 glow halo at ANY
    // zoom (both frozen while red owns motion); a live-family cluster with NO
    // member currently up is green but static, never haloed (motion never
    // claims a wholly-down site); an all-snapshot cluster stays ambient
    // snapshot blue, never animated (#368 — any live-family member makes the
    // cluster green). Colour is always paired with the count label.
    const renderClusterMarker = (cluster: CameraCluster, camScale: number): void => {
      const count = cluster.members.length;
      const position = Cesium.Cartesian3.fromDegrees(cluster.lon, cluster.lat);
      const register = clusterRegister(cluster.members);
      const color = Cesium.Color.fromCssColorString(
        register === 'ambientSnapshot' ? COVERAGE_SNAPSHOT : COVERAGE_LIVE,
      );
      const baseSize = (CLUSTER_DOT_SIZE + Math.min(count, 40) * 0.4) * camScale;
      // #255 ruling (i) corollary — clusters bypassed the per-camera zoom gate
      // (the QA 6,094 mush): gate the marker at the most restrictive member's
      // tier far so a stale grouping never outlives its members between
      // re-clusters. Membership itself is tier-filtered in rebuildCameraEntities.
      const ddc = new Cesium.DistanceDisplayCondition(0, clusterVisibilityFarM(cluster.members));
      // #267 — the cluster's glow halo swells around the count dot (per-cluster
      // size callback: base size varies with the count; alpha shares the global
      // callback). Same entity, third graphic — removed with the cluster.
      const clusterGlowPulse =
        register === 'liveBreathing' && glowSprite
          ? new Cesium.CallbackProperty(
              () => greenGlowSize(baseSize, performance.now() / 1000, !greenMotionAllowedNow()),
              false,
            )
          : null;
      const entity = viewer.entities.add({
        id: `cameracluster:${cluster.id}`,
        position,
        billboard: clusterGlowPulse
          ? {
              image: glowSprite!,
              width: clusterGlowPulse,
              height: clusterGlowPulse,
              color: glowColorPulse,
              verticalOrigin: Cesium.VerticalOrigin.CENTER,
              disableDepthTestDistance: Number.POSITIVE_INFINITY,
              distanceDisplayCondition: ddc,
            }
          : undefined,
        point: {
          pixelSize:
            register === 'liveBreathing'
              ? new Cesium.CallbackProperty(
                  () =>
                    greenBreatheSize(baseSize, performance.now() / 1000, !greenMotionAllowedNow()),
                  false,
                )
              : baseSize,
          color: color.withAlpha(0.9),
          outlineColor: Cesium.Color.WHITE.withAlpha(0.85),
          outlineWidth: 2 * camScale,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          distanceDisplayCondition: ddc,
        },
        label: {
          text: String(count),
          font: `${Math.round(11 * camScale)}px sans-serif`,
          fillColor: Cesium.Color.WHITE,
          style: Cesium.LabelStyle.FILL,
          verticalOrigin: Cesium.VerticalOrigin.CENTER,
          horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          distanceDisplayCondition: ddc,
        },
      });
      cameraClusters.set(cluster.id, { entity, cluster });
    };

    // Clear + rebuild all camera + cluster entities from the last fetched feature
    // set, re-clustering against the current zoom's merge radius. Called on each
    // fetch (new features) and on zoom (same features, tighter/looser radius).
    const rebuildCameraEntities = (): void => {
      viewer.entities.suspendEvents();
      for (const c of cameras.values()) {
        viewer.entities.remove(c.marker);
        viewer.entities.remove(c.footprint);
        if (c.glow) viewer.entities.remove(c.glow);
        if (c.glyph) viewer.entities.remove(c.glyph);
      }
      cameras.clear();
      for (const { entity } of cameraClusters.values()) viewer.entities.remove(entity);
      cameraClusters.clear();
      // Markers are recreated, so the old pop-out halos would dangle with stale
      // ids — drop them; applyPulse re-spawns for cameras still in view.
      for (const ring of popoutRings.values()) viewer.entities.remove(ring);
      popoutRings.clear();
      // WALL profile (#132) scales the static camera geometry (snapshot dot,
      // video glyph); the live breathing dot + its #267 glow halo scale per
      // frame inside liveInnerPulse / singleGlowPulse. 1 in COP.
      const camScale = propsRef.current.markerScale ?? 1;
      // #255 founder ruling (i): cluster membership respects zoom-tier
      // visibility — only cameras individually visible at the current eye
      // height render or cluster (at country zoom: the live-video tier ONLY).
      // The whole-fleet feed was the QA 6,094-camera mush.
      const tierVisible = tierVisibleCameras(
        lastCameraFeatures,
        viewer.camera.positionCartographic.height,
      );
      lastTierVisibleCount = tierVisible.length;
      // #385 — cluster the live-video family and the traffic (snapshot) family
      // SEPARATELY so a live cam is never absorbed into a traffic cluster's count
      // (or vice-versa); #368's precedence only RECOLOURED a mixed cluster green,
      // it didn't stop the mix. #368 still holds for z-order: every camera entity
      // disables the depth test, so build order = z-order (last-added wins), and
      // orderClustersLiveLast enqueues all snapshot/ambient (blue) clusters first
      // and all live-family (green) clusters last — so green dots never vanish
      // under a flood of blue, and a breaking-up cluster reveals its green members
      // on top immediately.
      const clusters = orderClustersLiveLast(
        clusterCamerasByFamily(tierVisible, {
          clusterRadiusM: currentClusterRadiusM(),
          coLocatedRadiusM: CO_LOCATED_RADIUS_M,
        }),
      );
      for (const cluster of clusters) {
        if (cluster.members.length === 1) {
          renderSingleCamera(cluster.members[0]!, camScale);
        } else {
          renderClusterMarker(cluster, camScale);
        }
      }
      viewer.entities.resumeEvents();
      applyVisibility();
      // Markers are recreated on every refresh, so re-apply pulse state to the
      // new entities for any camera that still has an open pop-out.
      applyPulse();
      // #255 — the layer count badge reflects the tier-visible set (what the
      // operator can actually see at this zoom), not the raw fetch; re-cluster
      // on zoom updates it as tiers bloom in/out.
      propsRef.current.onCameraCountChange(lastTierVisibleCount, false);
    };

    const renderCameras = (features: CameraFeature[]): void => {
      lastCameraFeatures = features;
      rebuildCameraEntities();
    };

    const renderSensors = (features: SensorFeature[]): void => {
      viewer.entities.suspendEvents();
      for (const s of sensors.values()) viewer.entities.remove(s.marker);
      sensors.clear();
      for (const f of features) {
        const [lon, lat] = f.geometry.coordinates;
        const marker = viewer.entities.add({
          id: `sensor:${f.id}`,
          position: Cesium.Cartesian3.fromDegrees(lon, lat),
          point: {
            pixelSize: 6,
            color: Cesium.Color.fromCssColorString(SENSOR_WEATHER),
            outlineColor: Cesium.Color.fromCssColorString('#0b1020'),
            outlineWidth: 1,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
          },
        });
        sensors.set(f.id, { feature: f, marker });
      }
      viewer.entities.resumeEvents();
      applyVisibility();
    };

    // A response-asset feature is visible when its sublayer is on; Ontario
    // Corps partners + markers are further gated by the per-partner hide set
    // (the umbrella program row itself always shows when the layer is on).
    const assetVisible = (f: ResponseAssetFeature): boolean => {
      const p = propsRef.current;
      switch (f.properties.asset_class) {
        case 'fire_station':
          return p.showFireStations;
        case 'wildfire_base':
          return p.showWildfireBases;
        case 'aviation_base':
          return p.showAviationBases;
        case 'umbrella_org':
        case 'illustrative_marker': {
          if (!p.showOntarioCorps) return false;
          if (f.properties.parent_org_id === null) return true;
          const partner = f.properties.operator_org;
          return !partner || !p.hiddenPartners.has(partner);
        }
        default:
          return true;
      }
    };

    const applyAssetVisibility = (): void => {
      for (const { feature, entity } of assets.values()) {
        entity.show = assetVisible(feature);
      }
    };

    const reportAssetCounts = (features: ResponseAssetFeature[]): void => {
      const counts: AssetCounts = { ...EMPTY_ASSET_COUNTS };
      for (const f of features) {
        switch (f.properties.asset_class) {
          case 'fire_station':
            counts.fireStations += 1;
            break;
          case 'wildfire_base':
            counts.wildfireBases += 1;
            break;
          case 'aviation_base':
            counts.aviationBases += 1;
            break;
          case 'umbrella_org':
          case 'illustrative_marker':
            counts.ontarioCorps += 1;
            break;
        }
      }
      propsRef.current.onAssetCountsChange(counts);
    };

    const renderAssets = (features: ResponseAssetFeature[]): void => {
      viewer.entities.suspendEvents();
      for (const { entity } of assets.values()) viewer.entities.remove(entity);
      assets.clear();
      for (const f of features) {
        const [lon, lat] = f.geometry.coordinates;
        const style = classifyAsset(f.properties);
        const icon = assetIcon(style);
        const position = Cesium.Cartesian3.fromDegrees(lon, lat);
        // Zoom-tier culling (§7): bounded classes cull past their `far` so the
        // country view stays clean; unbounded classes (aviation/wildfire) omit it.
        const far = assetDistanceFar(f.properties.asset_class);
        const ddc = far ? new Cesium.DistanceDisplayCondition(0, far) : undefined;
        const entity = viewer.entities.add({
          id: `asset:${f.id}`,
          position,
          ...(icon
            ? {
                billboard: {
                  image: icon,
                  width: style.size,
                  height: style.size,
                  // Ambient CAHS framed assets cap at ≤0.85 α (Law 4 / PART 2.3);
                  // every other asset renders opaque, unchanged. Static — no
                  // CallbackProperty, so the marker adds zero animated pixels.
                  color:
                    style.alpha != null ? Cesium.Color.WHITE.withAlpha(style.alpha) : undefined,
                  disableDepthTestDistance: Number.POSITIVE_INFINITY,
                  verticalOrigin: Cesium.VerticalOrigin.CENTER,
                  distanceDisplayCondition: ddc,
                },
              }
            : {
                point: {
                  pixelSize: style.size * 0.7,
                  color: Cesium.Color.fromCssColorString(style.color),
                  outlineColor: Cesium.Color.fromCssColorString('#0b1020'),
                  outlineWidth: 1,
                  disableDepthTestDistance: Number.POSITIVE_INFINITY,
                  distanceDisplayCondition: ddc,
                },
              }),
        });
        assets.set(f.id, { feature: f, entity });
      }
      viewer.entities.resumeEvents();
      applyAssetVisibility();
      reportAssetCounts(features);
    };

    const refreshAssets = (): void => {
      const rect = viewer.camera.computeViewRectangle();
      if (!rect) return;
      const bbox = rectangleToBbox(rect);
      const p = propsRef.current;
      const classes: string[] = [];
      if (p.showFireStations) classes.push('fire_station');
      if (p.showWildfireBases) classes.push('wildfire_base');
      if (p.showAviationBases) classes.push('aviation_base');
      if (p.showOntarioCorps) classes.push('umbrella_org', 'illustrative_marker');
      if (classes.length === 0) {
        for (const { entity } of assets.values()) viewer.entities.remove(entity);
        assets.clear();
        propsRef.current.onAssetCountsChange({ ...EMPTY_ASSET_COUNTS });
        return;
      }
      if (inflightAssets) inflightAssets.abort();
      const ctl = new AbortController();
      inflightAssets = ctl;
      fetchResponseAssets(bbox, classes, ctl.signal)
        .then((data) => {
          if (destroyed || ctl.signal.aborted) return;
          renderAssets(data.features);
        })
        .catch((err) => {
          if ((err as { name?: string }).name === 'AbortError') return;
          console.error('fetchResponseAssets failed', err);
        });
    };

    const aircraftVisible = (f: AircraftFeature): boolean => {
      const p = propsRef.current;
      if (!p.showLiveAircraft) return false;
      const role = f.properties.aircraft_role;
      return !role || !p.hiddenAircraftRoles.has(role);
    };

    const applyAircraftVisibility = (): void => {
      for (const e of aircraft.values()) e.entity.show = aircraftVisible(e.feature);
    };

    // Lay down the SampledPositionProperty plan for a fresh fix (D2 §3.3). We
    // pin the marker where it is now, glide it onto the new truth, then project
    // it forward along heading+speed — so a new fix reconciles smoothly instead
    // of teleporting by the dead-reckon error (the old per-poll snap). Cesium
    // HOLDs past the last sample, capping drift if the feed goes quiet.
    const FAR_FUTURE_S = 1e9;
    const applyAircraftFix = (
      entry: AircraftEntry,
      lat: number,
      lon: number,
      altM: number,
      headingDeg: number | null,
      speedKt: number | null,
      firstFix: boolean,
    ): void => {
      const now = Cesium.JulianDate.now();
      let displayed: PositionAtTime | null = null;
      if (!firstFix) {
        const cur = entry.position.getValue(now);
        if (cur) {
          const carto = Cesium.Cartographic.fromCartesian(cur);
          displayed = {
            lat: Cesium.Math.toDegrees(carto.latitude),
            lon: Cesium.Math.toDegrees(carto.longitude),
            alt: carto.height,
          };
        }
        // Drop the previously-projected (future) samples + trim old history so a
        // fix re-plans cleanly and the sample buffer stays bounded.
        const future = Cesium.JulianDate.addSeconds(now, FAR_FUTURE_S, new Cesium.JulianDate());
        entry.position.removeSamples(new Cesium.TimeInterval({ start: now, stop: future }));
        const past = Cesium.JulianDate.addSeconds(
          now,
          -(AIRCRAFT_HISTORY_SECONDS + FAR_FUTURE_S),
          new Cesium.JulianDate(),
        );
        const keepFrom = Cesium.JulianDate.addSeconds(
          now,
          -AIRCRAFT_HISTORY_SECONDS,
          new Cesium.JulianDate(),
        );
        entry.position.removeSamples(new Cesium.TimeInterval({ start: past, stop: keepFrom }));
      }
      const samples = planMotionSamples(displayed, { lat, lon, alt: altM }, headingDeg, speedKt);
      for (const s of samples) {
        const t = Cesium.JulianDate.addSeconds(now, s.dt, new Cesium.JulianDate());
        entry.position.addSample(t, Cesium.Cartesian3.fromDegrees(s.lon, s.lat, s.alt));
      }
    };

    // Freeze a now-stale aircraft where it currently appears (speed 0 → the plan
    // projects to the same point), so a quiet transponder doesn't keep gliding
    // for the full dead-reckon horizon away from its last known position.
    const freezeAircraft = (entry: AircraftEntry): void => {
      const cur = entry.position.getValue(Cesium.JulianDate.now());
      if (!cur) return;
      const carto = Cesium.Cartographic.fromCartesian(cur);
      applyAircraftFix(
        entry,
        Cesium.Math.toDegrees(carto.latitude),
        Cesium.Math.toDegrees(carto.longitude),
        carto.height,
        entry.headingDeg,
        0,
        false,
      );
    };

    // Billboard pixel size per #265 size-tier ruling. The tracking tier picks
    // the base size (live LARGER, based/stale SMALLER — reinforcing the #245
    // alpha ladder); the WALL markerScale (≈2×, 1 in COP) multiplies for
    // room-distance read, so aircraft finally honour the wall scale like every
    // other marker. WALL + demo use the bolder spread (mandatory there); the
    // operator COP uses the restrained spread (within the §3B.8 ambient law).
    // The tier is resolved per frame so a live→stale transition shrinks the
    // marker live; demo-mode is read once at build time (matching the rotor-spin
    // flag) so the per-frame callback never re-parses the URL / localStorage.
    const demoSpread = isDemoMode();
    const aircraftBillboardSize = (status: string): number => {
      const scale = propsRef.current.markerScale ?? 1;
      const profile = scale > 1 || demoSpread ? 'bold' : 'cop';
      return aircraftStatusSize(status, profile) * scale;
    };

    // Reconcile (not clear+rebuild) so interpolation samples survive across
    // polls: an unchanged last_seen_at keeps the marker gliding; only a fresh
    // server truth re-plans the trajectory.
    const renderAircraft = (features: AircraftFeature[]): void => {
      viewer.entities.suspendEvents();
      const seen = new Set<string>();
      for (const f of features) {
        seen.add(f.id);
        const [lon, lat] = f.geometry.coordinates;
        const altM = f.properties.altitude_m ?? DEFAULT_AIRCRAFT_ALTITUDE_M;
        const status = f.properties.tracking_status;
        const isQuiet = status !== 'live';
        const existing = aircraft.get(f.id);
        if (existing) {
          existing.feature = f;
          const wasQuiet = existing.status !== 'live';
          existing.status = status;
          if (f.properties.last_seen_at !== existing.lastSeenAt) {
            existing.headingDeg = f.properties.heading_deg;
            existing.speedKt = f.properties.speed_kt;
            existing.altM = altM;
            existing.lastSeenAt = f.properties.last_seen_at;
            applyAircraftFix(
              existing,
              lat,
              lon,
              altM,
              f.properties.heading_deg,
              f.properties.speed_kt,
              false,
            );
          } else if (isQuiet && !wasQuiet) {
            // Newly quiet, no fresh truth → stop it where it is.
            freezeAircraft(existing);
          }
          continue;
        }
        const style = classifyAircraft(
          f.properties.aircraft_role,
          f.properties.aircraft_type,
          f.properties.aircraft_type,
        );
        // Solid one-colour airframe silhouette (#201, founder option B — role
        // identity lives in the panel/legend, not the marker). The image is
        // static per type; heading is applied via the billboard's native
        // `rotation` below, so one cached texture serves every heading.
        const icon = aircraftSilhouetteUrl(f.properties.aircraft_type);
        const position = new Cesium.SampledPositionProperty();
        // Linear interpolation (no overshoot through turns) + HOLD at the ends so
        // a marker glides to a stop rather than extrapolating off the map.
        position.setInterpolationOptions({
          interpolationDegree: 1,
          interpolationAlgorithm: Cesium.LinearApproximation,
        });
        position.forwardExtrapolationType = Cesium.ExtrapolationType.HOLD;
        position.backwardExtrapolationType = Cesium.ExtrapolationType.HOLD;
        const entry: AircraftEntry = {
          feature: f,
          entity: undefined as unknown as Cesium.Entity,
          position,
          headingDeg: f.properties.heading_deg,
          speedKt: f.properties.speed_kt,
          altM,
          lastSeenAt: f.properties.last_seen_at,
          status,
          kind: style.kind,
          modelShown: false,
        };
        applyAircraftFix(
          entry,
          lat,
          lon,
          altM,
          f.properties.heading_deg,
          f.properties.speed_kt,
          true,
        );
        const entity = viewer.entities.add({
          id: `aircraft:${f.id}`,
          position,
          // Orientation from interpolated velocity — the glTF model noses along
          // its actual track (the billboard ignores this; it rotates in screen
          // space via `rotation` below).
          orientation: new Cesium.VelocityOrientationProperty(position),
          billboard: {
            image: icon,
            // Tier-stepped size (#265): live LARGER, based/stale SMALLER, ×WALL
            // markerScale. A CallbackProperty (cheap arithmetic, image unchanged)
            // so a live→stale transition shrinks the marker without a rebuild.
            width: new Cesium.CallbackProperty(
              () => aircraftBillboardSize(aircraft.get(f.id)?.status ?? 'live'),
              false,
            ),
            height: new Cesium.CallbackProperty(
              () => aircraftBillboardSize(aircraft.get(f.id)?.status ?? 'live'),
              false,
            ),
            // Point the nose-up silhouette along the live track. Recomputed per
            // frame from the heading (cheap numeric property; the image itself
            // never changes), so it rotates smoothly with interpolation.
            rotation: new Cesium.CallbackProperty(
              () => headingToBillboardRotation(aircraft.get(f.id)?.headingDeg ?? null),
              false,
            ),
            // Screen-aligned rotation (default axis) — heading is a compass
            // bearing, not an ECEF direction.
            alignedAxis: Cesium.Cartesian3.ZERO,
            // Quiet aircraft dim by tier: stale 40%, based 22% (#245 ladder).
            color: new Cesium.CallbackProperty(() => {
              const e = aircraft.get(f.id);
              return Cesium.Color.WHITE.withAlpha(aircraftStatusAlpha(e?.status ?? 'live'));
            }, false),
            // Subtle declutter: full size near, ~55% at continental range. Not an
            // animation (zoom-responsive scale), so it stays within motion budget.
            scaleByDistance: new Cesium.NearFarScalar(2.0e5, 1.0, 4.0e6, 0.55),
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            verticalOrigin: Cesium.VerticalOrigin.CENTER,
          },
          // TRUE-3D LOD model (hidden until the LOD pass promotes it). Unlit,
          // tinted to the single fleet gold (#13 — the glTF LOD matches the
          // billboard so a zoomed-in aircraft is the same gold, not a per-role
          // hue); helicopters carry the rotor-spin clip, played only in
          // demo-mode (the shared demo flag) — operational mode keeps static
          // rotors, within the motion budget.
          model: new Cesium.ModelGraphics({
            uri: aircraftModelUri(style.kind, AIRCRAFT_FLEET_GOLD),
            minimumPixelSize: 64,
            maximumScale: 20000,
            runAnimations: isDemoMode() && style.kind === 'heli',
            // Default HIGHLIGHT blend: WHITE keeps the baked fleet-gold tint,
            // alpha dims a quiet airframe by tier (mirrors the billboard cue).
            color: new Cesium.CallbackProperty(() => {
              const e = aircraft.get(f.id);
              return Cesium.Color.WHITE.withAlpha(aircraftStatusAlpha(e?.status ?? 'live'));
            }, false),
            show: false,
          }),
        });
        entry.entity = entity;
        aircraft.set(f.id, entry);
      }
      for (const [id, e] of aircraft) {
        if (!seen.has(id)) {
          viewer.entities.remove(e.entity);
          aircraft.delete(id);
        }
      }
      viewer.entities.resumeEvents();
      applyAircraftVisibility();
      updateAircraftLod();
      propsRef.current.onAircraftCountChange(aircraft.size);
    };

    // LOD: promote the nearest in-range aircraft (up to the model budget) to the
    // 3D glTF model and demote the rest to the cheap billboard. Bounds the glTF
    // count so framerate holds with many aircraft. Throttled (see preUpdate).
    const lodScratch: Array<{ entry: AircraftEntry; dist: number }> = [];
    const updateAircraftLod = (): void => {
      const cameraPos = viewer.camera.positionWC;
      const t = viewer.clock.currentTime;
      lodScratch.length = 0;
      for (const e of aircraft.values()) {
        if (!e.entity.show) continue;
        const pos = e.position.getValue(t);
        if (!pos) continue;
        const dist = Cesium.Cartesian3.distance(cameraPos, pos);
        if (dist <= AIRCRAFT_MODEL_RANGE_M) lodScratch.push({ entry: e, dist });
      }
      lodScratch.sort((a, b) => a.dist - b.dist);
      const promote = new Set<AircraftEntry>();
      for (let i = 0; i < Math.min(AIRCRAFT_MODEL_BUDGET, lodScratch.length); i++)
        promote.add(lodScratch[i]!.entry);
      for (const e of aircraft.values()) {
        const wantModel = promote.has(e);
        if (wantModel === e.modelShown) continue;
        e.modelShown = wantModel;
        if (e.entity.billboard) e.entity.billboard.show = new Cesium.ConstantProperty(!wantModel);
        if (e.entity.model) e.entity.model.show = new Cesium.ConstantProperty(wantModel);
      }
    };

    const refreshAircraft = (): void => {
      if (!propsRef.current.showLiveAircraft) {
        if (aircraft.size > 0) {
          for (const e of aircraft.values()) viewer.entities.remove(e.entity);
          aircraft.clear();
          propsRef.current.onAircraftCountChange(0);
        }
        return;
      }
      const rect = viewer.camera.computeViewRectangle();
      if (!rect) return;
      const bbox = rectangleToBbox(rect);
      if (inflightAircraft) inflightAircraft.abort();
      const ctl = new AbortController();
      inflightAircraft = ctl;
      fetchAircraft(bbox, ctl.signal)
        .then((data) => {
          if (destroyed || ctl.signal.aborted) return;
          renderAircraft(data.features);
        })
        .catch((err) => {
          if ((err as { name?: string }).name === 'AbortError') return;
          console.error('fetchAircraft failed', err);
        });
    };

    const refresh = async (): Promise<void> => {
      const rect = viewer.camera.computeViewRectangle();
      if (!rect) return;
      const bbox = rectangleToBbox(rect);
      // Report the settled view bbox so the SITREP "Current map view" scope has it.
      propsRef.current.onBboxChange?.(bbox);
      const { showCameras, showSensors, smokePriorityOnly } = propsRef.current;

      if (showCameras) {
        if (inflightCameras) inflightCameras.abort();
        const ctl = new AbortController();
        inflightCameras = ctl;
        propsRef.current.onCameraCountChange(lastTierVisibleCount, true);
        fetchCameras(bbox, ctl.signal, { smokePriority: smokePriorityOnly })
          .then((data) => {
            if (destroyed || ctl.signal.aborted) return;
            // renderCameras → rebuildCameraEntities reports the tier-visible
            // count (#255) — the badge never shows the raw fetch size.
            renderCameras(data.features);
          })
          .catch((err) => {
            if ((err as { name?: string }).name === 'AbortError') return;
            console.error('fetchCameras failed', err);
            propsRef.current.onCameraCountChange(lastTierVisibleCount, false);
          });
      }
      if (showSensors) {
        if (inflightSensors) inflightSensors.abort();
        const ctl = new AbortController();
        inflightSensors = ctl;
        propsRef.current.onSensorCountChange(sensors.size, true);
        fetchSensors(bbox, ctl.signal)
          .then((data) => {
            if (destroyed || ctl.signal.aborted) return;
            renderSensors(data.features);
            propsRef.current.onSensorCountChange(data.features.length, false);
          })
          .catch((err) => {
            if ((err as { name?: string }).name === 'AbortError') return;
            console.error('fetchSensors failed', err);
            propsRef.current.onSensorCountChange(sensors.size, false);
          });
      }
      refreshAssets();
      refreshAircraft();
      // Fire Risk layer owns its own 300ms debounce; only fetch when visible.
      if (propsRef.current.showFireRisk) firesLayer.requestRefresh(bbox);
      // High-Risk Hotspots layer (E.2.3) — same own-debounce pattern.
      if (propsRef.current.showHighRiskHotspots) highRiskLayer.requestRefresh(bbox);
      // E.W.0.5 — Raw Hot Spots — same own-debounce pattern.
      if (propsRef.current.showRawHotspots) rawHotspotsLayer.requestRefresh(bbox);
      // #370 — Fusion Candidates — same own-debounce pattern.
      if (propsRef.current.showFusionCandidates) fusionCandidatesLayer.requestRefresh(bbox);
      // E.W.5 — Camera Smoke Detections — same own-debounce pattern.
      if (propsRef.current.showCameraSmoke) cameraSmokeLayer.requestRefresh(bbox);
      // Power Outages — same own-debounce pattern.
      if (propsRef.current.showPowerOutages) powerOutagesLayer.requestRefresh(bbox);
      // Road Closures — same own-debounce pattern.
      if (propsRef.current.showRoadClosures) roadClosuresLayer.requestRefresh(bbox);
      // Estimated Comms Degradation (#109) — same own-debounce pattern.
      if (propsRef.current.showCommsDegradationZones)
        commsDegradationZonesLayer.requestRefresh(bbox);
      // Extreme Weather Warnings (EWW) CAP polygons — same own-debounce pattern.
      if (propsRef.current.showExtremeWeatherWarnings) extremeWeatherLayer.requestRefresh(bbox);
      // Public-ArcGIS category renders (#213) — the manager refreshes each visible
      // category layer (own-debounce per layer).
      arcgisLayers.requestRefresh(bbox);
      // #445-(3) — operator-declared evac orders (own-debounce).
      if (propsRef.current.showOperatorEvacOrders) operatorEvacLayer.requestRefresh(bbox);
      // L.2.2 — smoke_detected anomalies ride the Anomaly Queue toggle.
      if (propsRef.current.showHighRiskHotspots) smokeAnomaliesLayer.requestRefresh(bbox);
      // Ice Storm Fusion Anomaly rides the Anomaly Queue toggle.
      if (propsRef.current.showHighRiskHotspots) iceAnomaliesLayer.requestRefresh(bbox);
      if (propsRef.current.showHighRiskHotspots) floodAnomaliesLayer.requestRefresh(bbox);
      if (propsRef.current.showHydrometricGauges) hydrometricGaugesLayer.requestRefresh(bbox);
      // #177/#336 — the 3D flood buildings/water + camera viewshed show-gates now run in
      // scheduleRefresh (the moveEnd handler), not here: this body early-returns above when
      // computeViewRectangle() is undefined at country zoom, which used to skip the gate
      // and leak the buildings nationally. They are pure show-toggles, no fetch.
      // L.2.2 — marine vessels ride the Aircraft & Marine toggle.
      if (propsRef.current.showLiveAircraft) marineLayer.requestRefresh(bbox);
      // L.2.2 — drone coverage.
      if (propsRef.current.showDroneCoverage) droneCoverageLayer.requestRefresh(bbox);
      // W.2 — Vulnerable Communities ride the Flood/Ice-Storm toggles (OR).
      if (propsRef.current.showCommunities) communitiesLayer.requestRefresh(bbox);
      // R.4.2 — Staging Areas.
      if (propsRef.current.showStagingAreas) stagingAreasLayer.requestRefresh(bbox);
    };

    // Stable-view re-poll of just the Anomaly-Queue map layers (Colour Law 3 /
    // #129): re-fetch the CURRENT view so an ack recolours to ACK-STEEL, a re-alarm
    // returns to URGENT-RED, and a cleared marker ghosts — all without a camera
    // nudge. Reuses the current view rectangle so the ghost tracker's bbox key is
    // stable. No-ops while the camera has no settled rectangle.
    const refreshAnomalies = (): void => {
      const rect = viewer.camera.computeViewRectangle();
      if (!rect) return;
      const bbox = rectangleToBbox(rect);
      if (propsRef.current.showHighRiskHotspots) {
        highRiskLayer.requestRefresh(bbox);
        smokeAnomaliesLayer.requestRefresh(bbox);
        iceAnomaliesLayer.requestRefresh(bbox);
        floodAnomaliesLayer.requestRefresh(bbox);
      }
    };

    // Toggle the Fire Risk layer's visibility; fetch on turn-on if we have a
    // view rectangle (the layer no-ops a refresh until the camera settles).
    const applyFireVisibility = (): void => {
      const show = propsRef.current.showFireRisk;
      firesLayer.setVisible(show);
      if (show) {
        const rect = viewer.camera.computeViewRectangle();
        if (rect) firesLayer.requestRefresh(rectangleToBbox(rect));
      }
    };

    // Same pattern for the High-Risk Hotspots layer (E.2.3).
    const applyHighRiskVisibility = (): void => {
      const show = propsRef.current.showHighRiskHotspots;
      highRiskLayer.setVisible(show);
      if (show) {
        const rect = viewer.camera.computeViewRectangle();
        if (rect) highRiskLayer.requestRefresh(rectangleToBbox(rect));
      }
    };

    // E.W.0.5 — Raw Hot Spots — same show/fetch-on-turn-on (the layer's own
    // distanceDisplayCondition handles the regional-zoom render gate).
    const applyRawHotspotsVisibility = (): void => {
      const show = propsRef.current.showRawHotspots;
      rawHotspotsLayer.setVisible(show);
      if (show) {
        const rect = viewer.camera.computeViewRectangle();
        if (rect) rawHotspotsLayer.requestRefresh(rectangleToBbox(rect));
      }
    };

    // #370 — Fusion Candidates — same show/fetch-on-turn-on (the layer's own
    // distanceDisplayCondition handles the regional-zoom render gate).
    const applyFusionCandidatesVisibility = (): void => {
      const show = propsRef.current.showFusionCandidates;
      fusionCandidatesLayer.setVisible(show);
      if (show) {
        const rect = viewer.camera.computeViewRectangle();
        if (rect) fusionCandidatesLayer.requestRefresh(rectangleToBbox(rect));
      }
    };

    // E.W.5 — Camera Smoke Detections — same show/fetch-on-turn-on (the layer's own
    // distanceDisplayCondition handles the regional-zoom render gate).
    const applyCameraSmokeVisibility = (): void => {
      const show = propsRef.current.showCameraSmoke;
      cameraSmokeLayer.setVisible(show);
      if (show) {
        const rect = viewer.camera.computeViewRectangle();
        if (rect) cameraSmokeLayer.requestRefresh(rectangleToBbox(rect));
      }
    };

    // Road Closures "Restrictions" mute — re-render the loaded closures from cache
    // (no refetch) when the sub-toggle flips, so restriction dots vanish/return
    // immediately while full closures stay.
    const applyRoadClosuresRestrictions = (): void => {
      roadClosuresLayer.refilter();
    };

    // Power Outages (Ice Storm Risk) — same show/fetch-on-turn-on (the layer's own
    // distanceDisplayCondition handles the regional-zoom render gate).
    const applyPowerOutagesVisibility = (): void => {
      const show = propsRef.current.showPowerOutages;
      powerOutagesLayer.setVisible(show);
      if (show) {
        const rect = viewer.camera.computeViewRectangle();
        if (rect) powerOutagesLayer.requestRefresh(rectangleToBbox(rect));
      }
    };

    // Road Closures (Ice Storm Risk) — same show/fetch-on-turn-on (the layer's own
    // distanceDisplayCondition handles the regional-zoom render gate).
    const applyRoadClosuresVisibility = (): void => {
      const show = propsRef.current.showRoadClosures;
      roadClosuresLayer.setVisible(show);
      if (show) {
        const rect = viewer.camera.computeViewRectangle();
        if (rect) roadClosuresLayer.requestRefresh(rectangleToBbox(rect));
      }
    };

    // Estimated Comms Degradation (#109) — same show/fetch-on-turn-on (the layer's
    // own distanceDisplayCondition handles the regional-zoom render gate).
    const applyCommsDegradationVisibility = (): void => {
      const show = propsRef.current.showCommsDegradationZones;
      commsDegradationZonesLayer.setVisible(show);
      if (show) {
        const rect = viewer.camera.computeViewRectangle();
        if (rect) commsDegradationZonesLayer.requestRefresh(rectangleToBbox(rect));
      }
    };

    // Extreme Weather Warnings CAP polygons (EWW) — same show/fetch-on-turn-on.
    const applyExtremeWeatherVisibility = (): void => {
      const show = propsRef.current.showExtremeWeatherWarnings;
      extremeWeatherLayer.setVisible(show);
      if (show) {
        const rect = viewer.camera.computeViewRectangle();
        if (rect) extremeWeatherLayer.requestRefresh(rectangleToBbox(rect));
      }
    };

    // Public-ArcGIS category renders (#213) — apply each category's show state and
    // fetch any that just became visible (each layer's own distanceDisplayCondition
    // handles the regional-zoom render gate).
    const applyArcgisVisibility = (): void => {
      const rect = viewer.camera.computeViewRectangle();
      arcgisLayers.applyVisibility(rect ? rectangleToBbox(rect) : undefined);
    };

    // Provincial boundaries (Context group, L.1) — pure show/hide; the entities
    // are already loaded (or will apply state on resolve via the install above).
    const applyBoundaryVisibility = (): void => {
      boundaryLayer?.setVisible(propsRef.current.showProvincialBoundaries);
    };

    // FWI heatmap (Weather group, E.2.2) — pure show/hide of the imagery overlay.
    // Tiles are fetched lazily by Cesium on turn-on; no bbox refresh needed.
    const applyFwiVisibility = (): void => {
      fwiLayer.setVisible(propsRef.current.showFwiHeatmap);
    };

    // #212 — WMS raster overlays: pure show/hide per layer key. Tiles are fetched
    // lazily by Cesium on turn-on; no bbox refresh needed (Canada-scoped extent).
    const applyWmsOverlaysVisibility = (): void => {
      wmsOverlays.applyVisibility();
    };

    // L.2.2 — smoke_detected anomalies ride the Anomaly Queue toggle.
    const applySmokeAnomalyVisibility = (): void => {
      const show = propsRef.current.showHighRiskHotspots;
      smokeAnomaliesLayer.setVisible(show);
      if (show) {
        const rect = viewer.camera.computeViewRectangle();
        if (rect) smokeAnomaliesLayer.requestRefresh(rectangleToBbox(rect));
      }
    };

    // Ice Storm Fusion Anomaly rides the Anomaly Queue toggle (same as smoke).
    const applyIceAnomalyVisibility = (): void => {
      const show = propsRef.current.showHighRiskHotspots;
      iceAnomaliesLayer.setVisible(show);
      if (show) {
        const rect = viewer.camera.computeViewRectangle();
        if (rect) iceAnomaliesLayer.requestRefresh(rectangleToBbox(rect));
      }
    };

    // Flood Fusion Anomaly rides the Anomaly Queue toggle (same as smoke/ice).
    const applyFloodAnomalyVisibility = (): void => {
      const show = propsRef.current.showHighRiskHotspots;
      floodAnomaliesLayer.setVisible(show);
      if (show) {
        const rect = viewer.camera.computeViewRectangle();
        if (rect) floodAnomaliesLayer.requestRefresh(rectangleToBbox(rect));
      }
    };

    // Hydrometric Gauges (Flood Risk) — same show/fetch-on-turn-on (the layer's own
    // distanceDisplayCondition handles the regional-zoom render gate).
    const applyHydrometricGaugesVisibility = (): void => {
      const show = propsRef.current.showHydrometricGauges;
      hydrometricGaugesLayer.setVisible(show);
      if (show) {
        const rect = viewer.camera.computeViewRectangle();
        if (rect) hydrometricGaugesLayer.requestRefresh(rectangleToBbox(rect));
      }
    };

    // 3D Flood Inundation (#177): enabling swaps in World Terrain + OSM Buildings +
    // the water surface (and restores the base map on disable). The layer bounds
    // itself to the demo region via applyRegionGate (called on camera moves below).
    const applyFloodInundationVisibility = (): void => {
      floodInundationLayer.setEnabled(propsRef.current.showFloodInundation);
    };

    // #178/#262 viewshed-on-select: while the per-operator pref is ON, selecting
    // a camera acquires World Terrain + computes its footprint async; deselecting
    // releases both. Selection changes are pushed separately via applyViewshedCamera.
    const applyCameraViewshedVisibility = (): void => {
      cameraViewshedLayer.setCamera(propsRef.current.viewshedCamera);
      cameraViewshedLayer.setEnabled(propsRef.current.viewshedOnSelect);
    };
    const applyViewshedCamera = (): void => {
      cameraViewshedLayer.setCamera(propsRef.current.viewshedCamera);
    };

    // L.2.2 — marine vessels ride the Aircraft & Marine toggle.
    const applyMarineVisibility = (): void => {
      const show = propsRef.current.showLiveAircraft;
      marineLayer.setVisible(show);
      if (show) {
        const rect = viewer.camera.computeViewRectangle();
        if (rect) marineLayer.requestRefresh(rectangleToBbox(rect));
      }
    };

    // L.2.2 — drone coverage show/fetch + reachability re-paint on selection.
    const applyDroneCoverageVisibility = (): void => {
      const show = propsRef.current.showDroneCoverage;
      droneCoverageLayer.setVisible(show);
      if (show) {
        const rect = viewer.camera.computeViewRectangle();
        if (rect) droneCoverageLayer.requestRefresh(rectangleToBbox(rect));
      }
      // Re-paint coverage highlight against the current selected incident.
      droneCoverageLayer.setSelectedIncident(propsRef.current.selectedIncidentPoint ?? null);
    };

    // W.2 — Vulnerable Communities show/fetch-on-turn-on (mirrors marine/drone).
    const applyCommunityVisibility = (): void => {
      const show = propsRef.current.showCommunities;
      communitiesLayer.setVisible(show);
      if (show) {
        const rect = viewer.camera.computeViewRectangle();
        if (rect) communitiesLayer.requestRefresh(rectangleToBbox(rect));
      }
    };
    // W.2 — recolour community markers when App's status cache changes (no refetch).
    const recolorCommunities = (): void => communitiesLayer.recolor();

    // R.4.2 — Staging Areas show/fetch-on-turn-on (mirrors drone/community).
    const applyStagingAreasVisibility = (): void => {
      const show = propsRef.current.showStagingAreas;
      stagingAreasLayer.setVisible(show);
      if (show) {
        const rect = viewer.camera.computeViewRectangle();
        if (rect) stagingAreasLayer.requestRefresh(rectangleToBbox(rect));
      }
    };

    // R5 Slice 3 (#507) — Area Command toggle: show/hide + refresh-on-turn-on (the
    // layer already polls regardless of visibility for the picker count; the turn-on
    // refresh just makes a just-established Area Command appear without the poll wait).
    const applyAreaCommandVisibility = (): void => {
      const show = propsRef.current.showAreaCommand;
      areaCommandLayer.setVisible(show);
      if (show) void areaCommandLayer.refreshNow();
    };

    const scheduleRefresh = (): void => {
      // #195 — re-cluster instantly against the new zoom from the cached features
      // so clusters split/merge as you zoom without waiting for the debounced
      // refetch (the refetch below then refreshes the underlying feature set).
      if (propsRef.current.showCameras && lastCameraFeatures.length > 0) {
        rebuildCameraEntities();
      }
      // #336 — re-apply the 3D show-gates (flood buildings/water, camera viewshed) on
      // EVERY camera settle, here in the moveEnd handler rather than inside the debounced
      // refresh() below. refresh() early-returns when computeViewRectangle() is undefined
      // — exactly the country/zoomed-out regime — so the gate there never ran at country
      // zoom, leaving the global OSM Buildings tileset shown from when the camera was
      // in-region (grey buildings leaking nationally). These are pure show-toggles (no
      // fetch, no view rectangle needed), so they belong on the settle itself and apply
      // instantly without waiting on the debounce.
      if (propsRef.current.showFloodInundation) floodInundationLayer.applyRegionGate();
      if (propsRef.current.viewshedOnSelect) cameraViewshedLayer.applyRegionGate();
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => void refresh(), DEBOUNCE_MS);
    };

    const removeMoveEnd = viewer.camera.moveEnd.addEventListener(scheduleRefresh);

    // IMS audit context: the COP snapshot an action row needs — current bbox,
    // zoom height, and which layers are on. Built lazily at each action.
    const buildContext = (): Record<string, unknown> => {
      const rect = viewer.camera.computeViewRectangle();
      const p = propsRef.current;
      return {
        bbox: rect ? rectangleToBbox(rect) : null,
        zoom_height_m: Math.round(viewer.camera.positionCartographic.height),
        toggles: {
          cameras: p.showCameras,
          sensors: p.showSensors,
          fireRisk: p.showFireRisk,
          fireStations: p.showFireStations,
          wildfireBases: p.showWildfireBases,
          aviationBases: p.showAviationBases,
          ontarioCorps: p.showOntarioCorps,
          liveAircraft: p.showLiveAircraft,
          highRiskHotspots: p.showHighRiskHotspots,
          extremeWeatherWarnings: p.showExtremeWeatherWarnings,
          fwiHeatmap: p.showFwiHeatmap,
        },
      };
    };

    // Pan/zoom audit: a separate 1 s-debounced moveEnd listener (continuous drag/
    // zoom would otherwise emit a row per frame). Classify by height delta —
    // a meaningful altitude change is a zoom, otherwise a pan.
    let lastLoggedHeight = viewer.camera.positionCartographic.height;
    let panZoomTimer: ReturnType<typeof setTimeout> | null = null;
    const logPanZoom = (): void => {
      const height = viewer.camera.positionCartographic.height;
      const ratio = Math.abs(height - lastLoggedHeight) / Math.max(1, lastLoggedHeight);
      const action = ratio > 0.05 ? 'zoom_map' : 'pan_map';
      lastLoggedHeight = height;
      logAction(action, { zoom_height_m: Math.round(height) }, buildContext());
    };
    const removeMoveEndLog = viewer.camera.moveEnd.addEventListener(() => {
      if (panZoomTimer) clearTimeout(panZoomTimer);
      panZoomTimer = setTimeout(logPanZoom, 1000);
    });

    // #415 (Phase 6b) — co-located ACT-incident legibility. Resolve a Cesium entity
    // to ONE incident ref (family + feature) via each layer's own pick(), so the
    // seven act-incident families (hr/fc/cap/sm/ice/fl/ou) are the SINGLE source of
    // membership — ambient assets resolve to null and sit behind. fc opens the same
    // high-risk panel as hr. Ring/glyph/area sub-entities collapse to one ref (one
    // incident). Replaces the rejected #390 click-cycle: Cesium `point` markers can't
    // take a screen offset, so a stack is revealed by a "+N" badge + a click-to-list,
    // not by fanning the markers (founder ruling 2026-06-23).
    const incidentRefOf = (entity: unknown): ColocatedIncidentRef | null => {
      const hr = highRiskLayer.pick(entity);
      if (hr) return { kind: 'highRiskHotspot', feature: hr };
      const fc = fusionCandidatesLayer.pick(entity);
      if (fc) return { kind: 'highRiskHotspot', feature: fc };
      const cap = extremeWeatherLayer.pick(entity);
      if (cap) return { kind: 'capAlert', feature: cap };
      const sm = smokeAnomaliesLayer.pick(entity);
      if (sm) return { kind: 'smokeDetectionAnomaly', feature: sm };
      const ice = iceAnomaliesLayer.pick(entity);
      if (ice) return { kind: 'iceStormAnomaly', feature: ice };
      const fl = floodAnomaliesLayer.pick(entity);
      if (fl) return { kind: 'floodAnomaly', feature: fl };
      const ou = powerOutagesLayer.pick(entity);
      if (ou) return { kind: 'utilityOutage', feature: ou };
      return null;
    };
    // Stable per-incident key (family + id) — de-dupes a stack and a recompute.
    const refKey = (ref: ColocatedIncidentRef): string =>
      `${ref.kind}:${ref.feature.properties.id}`;
    // Sort weight for the list + badge anchor: IMS Response Level when the family
    // carries one (smoke + outages don't), else 0 — most urgent first.
    const priorityOf = (ref: ColocatedIncidentRef): number => {
      const p = (ref.feature.properties as { response_level?: number | null }).response_level;
      return typeof p === 'number' ? p : 0;
    };

    // ── Co-location SIDE-BY-SIDE fan (icon one-swing #564, founder re-ruling 2026-07-06) ──
    // The numbered "+N incidents here" stack badge is RETIRED. A MapView-owned pass: on every
    // camera-stop (and a 4 s tick, for incidents that arrive with no camera move), project the
    // rendered+visible ACT markers to screen, group screen-collisions (coLocatedClusters.ts —
    // pure), and fan each stack's members SIDE BY SIDE around the shared midpoint via a
    // horizontal billboard `pixelOffset` (unlocked by this swing converting every hazard family
    // to a billboard). The fan collapses on zoom-in automatically — when the true positions
    // separate past the pixel radius the next recompute finds no collision and zeroes the offsets.
    // Suppressed/ambient entities resolve to null in incidentRefOf and DDC-gated (country-zoom)
    // markers are skipped, so only what the operator can actually see is fanned. The click
    // drillPick disambiguation picker (below) is KEPT as the backstop for a tight fan.
    // The set of refKeys currently fanned (with an applied offset) — so a dissolved stack's
    // members get their offset RESET to zero on the next pass (even after a layer re-render
    // replaced the entity, since primaryBillboardByKey is rebuilt every pass).
    let fannedKeys = new Set<string>();
    // True only if the entity's primary graphic is actually rendered at this camera distance —
    // honours entity.show + the marker's distanceDisplayCondition (e.g. the L1/L2 regional-only
    // gate), so a hidden marker never joins a fan.
    const isMarkerVisibleNow = (
      entity: Cesium.Entity,
      world: Cesium.Cartesian3,
      now: Cesium.JulianDate,
    ): boolean => {
      if (entity.show === false) return false;
      const ddc =
        entity.point?.distanceDisplayCondition?.getValue(now) ??
        entity.billboard?.distanceDisplayCondition?.getValue(now);
      if (!ddc) return true;
      const dist = Cesium.Cartesian3.distance(viewer.camera.positionWC, world);
      return dist >= ddc.near && dist <= ddc.far;
    };
    const setFanOffset = (entity: Cesium.Entity | undefined, dx: number): void => {
      if (entity?.billboard) {
        entity.billboard.pixelOffset = new Cesium.ConstantProperty(new Cesium.Cartesian2(dx, 0));
      }
    };
    const recomputeCoLocation = (): void => {
      if (viewer.isDestroyed()) return;
      const now = viewer.clock.currentTime;
      // Founder NIGHT ruling 3 (2026-07-06): the side-by-side fan is a CLOSE-ZOOM declutter ONLY.
      // At country/national zoom (camera above the ceiling) it is DISABLED — co-located markers
      // render at their TRUE positions (overplot; the click-picker discloses the stack on click; a
      // PASSIVE national-zoom count badge is a flagged founder decision, see the run-log). THIS is
      // the round-2 band fix: the ungated national-zoom fan strung a 76-member
      // screen-cluster of in-Canada hotspots along one screen-Y across both oceans (measured
      // 2026-07-06 — 277 in-Canada RL≥3, 0 out-of-Canada). See fanEnabledAtHeight.
      const cameraHeightM = viewer.camera.positionCartographic.height;
      const fanOn = fanEnabledAtHeight(cameraHeightM);
      const points: ColocatedPoint[] = [];
      const seen = new Set<string>();
      // The primary BILLBOARD entity per incident (the movable marker the fan offsets). Built
      // separately from the collision dedup so a multi-entity family (e.g. a power outage: point
      // dot + chip billboard) records its billboard even though its FIRST entity is the point.
      // Built on EVERY pass (even when the fan is off) so a zoom-OUT past the ceiling can reset the
      // offsets a prior regional pass applied — snapping every marker back onto its true anchor.
      const primaryBillboardByKey = new Map<string, Cesium.Entity>();
      for (let i = 0; i < viewer.dataSources.length; i += 1) {
        const ds = viewer.dataSources.get(i);
        if (!ds.show) continue;
        for (const entity of ds.entities.values) {
          const ref = incidentRefOf(entity);
          if (!ref) continue;
          const key = refKey(ref);
          const world = entity.position?.getValue(now);
          if (!world) continue;
          // Record the primary billboard for EVERY incident entity — even one currently DDC-hidden —
          // so a zoom-OUT reset can ALWAYS find it. (Otherwise a fanned DDC-gated marker, e.g. an
          // outage or an L1/L2 hotspot, would keep a stale offset when it hides at country zoom and
          // never get reset until the layer happened to re-render.)
          if (entity.billboard && !primaryBillboardByKey.has(key)) {
            primaryBillboardByKey.set(key, entity);
          }
          // Only VISIBLE markers join the fan (honour entity.show + the DDC gate), and only when the
          // fan is on — at country/national zoom skip the screen-projection entirely (nothing fans;
          // the reset pass below snaps any prior fan back).
          if (!fanOn || seen.has(key) || !isMarkerVisibleNow(entity, world, now)) continue;
          const win = Cesium.SceneTransforms.worldToWindowCoordinates(viewer.scene, world);
          if (!win) continue;
          seen.add(key);
          points.push({ key, x: win.x, y: win.y, priority: priorityOf(ref) });
        }
      }
      // The pure fan decision (the structural closure of the round-2 band class): EMPTY at
      // country/national zoom, else each member's pixelOffset.x — groups screen-collisions and
      // fans clusters of 2..cap side-by-side, leaving over-cap clusters overplotted. The GATE (not
      // just the member cap) is unit-tested with an isolated pair in coLocatedClusters.test.ts.
      // (`points` is already empty when fanOn is false.)
      const offsetByKey = computeFanOffsets(points, cameraHeightM);
      const nowFanned = new Set<string>();
      for (const [key, dx] of offsetByKey) {
        setFanOffset(primaryBillboardByKey.get(key), dx);
        nowFanned.add(key);
      }
      // A member that LEFT the fan (stack dissolved on zoom-in, camera ROSE past the ceiling,
      // cluster exceeded the cap, or it departed) → reset its offset to zero so it snaps back to its
      // true position. If its billboard is NOT reachable this pass (its whole layer is toggled off,
      // so the ds is skipped) we can't reset it yet — CARRY the key forward so a later pass (when the
      // layer shows again) resets it, instead of forgetting it and stranding a stale offset.
      const carry = new Set(nowFanned);
      for (const key of fannedKeys) {
        if (nowFanned.has(key)) continue;
        const ent = primaryBillboardByKey.get(key);
        if (ent) setFanOffset(ent, 0);
        else carry.add(key); // unreachable this pass → retry next pass
      }
      fannedKeys = carry;
    };
    const removeBadgeRecompute = viewer.camera.moveEnd.addEventListener(recomputeCoLocation);
    // A 4 s tick catches incidents that ARRIVE with no camera move (a polled anomaly on a
    // stationary/wall view) and re-applies the fan to freshly re-rendered entities. Bounded work
    // (enumerate + offset a handful of fanned members), so it's motion-budget-safe.
    const badgeRecomputeTimer = setInterval(recomputeCoLocation, 4000);

    const handler = new Cesium.ScreenSpaceEventHandler(viewer.canvas);
    handler.setInputAction((click: Cesium.ScreenSpaceEventHandler.PositionedEvent) => {
      // Task #64 — while the sector draw tool is active, its own handler owns the
      // clicks (add vertex / finish / cancel); don't select or clear the panel.
      if (sectorDraw.isActive()) return;
      if (evacDraw.isActive()) return;
      // W.2 editor — same for the community place-a-point tool (its click places
      // the point); don't select or clear the panel underneath it.
      if (communityPlace.isActive()) return;
      // #415 (Phase 6b) — co-located incident disambiguation. From ONE drillPick
      // build the distinct ACT-incident stack under the cursor (topmost-first); if
      // N≥2 incidents collide here they're too tight to click individually on the
      // map, so open the "N incidents here → pick one" list instead of selecting
      // blind (the visible "+N" badge already signalled the stack). N≤1 falls through
      // to the normal topmost pick below, so the common single-marker case is
      // unchanged. Replaces the rejected #390 click-cycle.
      // depth 32 (not 16): one incident can be up to 3 entities (dot + glyph + L4 ring),
      // so a deep 7-family stack would truncate at 16 and undercount the panel vs the
      // badge (§G fix #5). drillPick only runs on a click, so the deeper pick is cheap.
      const drilled = viewer.scene.drillPick(click.position, 32);
      const refByKey = new Map<string, ColocatedIncidentRef>();
      const stackedRefs: ColocatedIncidentRef[] = [];
      for (const hit of drilled) {
        const ref = incidentRefOf((hit as { id?: unknown } | undefined)?.id);
        if (ref) {
          const key = refKey(ref);
          if (!refByKey.has(key)) {
            refByKey.set(key, ref);
            stackedRefs.push(ref);
          }
        }
      }
      if (stackedRefs.length >= 2) {
        const ordered = [...stackedRefs].sort((a, b) => priorityOf(b) - priorityOf(a));
        propsRef.current.onSelectIncidentCluster(ordered);
        return;
      }
      // #348a — the translucent drone-coverage FILL ellipse must not steal clicks
      // from a fire / hotspot / incident inside the same coverage bubble. Demote it:
      // if the topmost pick is the fill, drill for any entity beneath and prefer it
      // (the coverage PIN and the fill itself remain selectable as fallbacks).
      const topmostPick = preferNonCoverageFillPick(
        viewer.scene.pick(click.position),
        () => drilled,
      );
      // Council §A "OC-fire unclickable" — the SECOND half of the ruling: TRY FIRES FIRST
      // in the pick cycle (Phase 5a / #409 shipped only the suppression half). When the
      // topmost entity is a redundant fire-corroboration marker (a high-risk hotspot /
      // fusion candidate / raw hotspot) stacked on a co-located fire, the fire is the
      // operator's object — re-point the pick to the fire beneath so the FireBody opens,
      // not the dot. Reads the already-computed `drilled` stack (no extra pick), runs AFTER
      // the #415 cluster early-return + #348a coverage-fill demotion, and is scoped to the
      // hotspot family + the tight 1 km co-location radius so it never steals a camera /
      // community / aircraft click. Covers EVERY fire stage + EVERY hotspot layer in one
      // place (suppression only de-clutters OC-FUL + high-risk), and backstops the
      // first-paint race where the dot isn't yet suppressed. #439.
      const fireUnderHotspot = pickFireUnderCoLocatedHotspot(topmostPick, drilled, {
        hotspotPositionOf: (entity) => {
          const hr = entity ? highRiskLayer.pick(entity) : null;
          if (hr) return hr.geometry.coordinates;
          const fusion = entity ? fusionCandidatesLayer.pick(entity) : null;
          if (fusion) return fusion.geometry.coordinates;
          const raw = entity ? rawHotspotsLayer.pick(entity) : null;
          if (raw) return raw.geometry.coordinates;
          return null;
        },
        fireCentroidOf: (entity) => {
          const f = entity ? firesLayer.pick(entity) : null;
          return f ? f.properties.centroid : null;
        },
      });
      const picked = fireUnderHotspot ?? topmostPick;
      // #474 — the four coloured warning-zone selections, factored so the topmost
      // pick (below) AND the whole-footprint drill fallback (before the deselect)
      // dispatch IDENTICALLY (same audit verb + side panel). No incident zoom: zones
      // are regional areas, not 20 km point incidents.
      const selectCapAlert = (f: CapAlertFeature): void => {
        logAction(
          'click_extreme_weather',
          { cap_id: f.properties.id, event: f.properties.event, severity: f.properties.severity },
          buildContext(),
        );
        propsRef.current.onSelectCapAlert(f);
      };
      const selectOutage = (f: UtilityOutageFeature): void => {
        logAction(
          'click_utility_outage',
          { outage_id: f.properties.id, utility: f.properties.utility },
          buildContext(),
        );
        propsRef.current.onSelectUtilityOutage(f);
      };
      const selectRoadClosure = (f: RoadClosureFeature): void => {
        logAction(
          'click_road_closure',
          { closure_id: f.properties.id, province: f.properties.province },
          buildContext(),
        );
        propsRef.current.onSelectRoadClosure(f);
      };
      const selectCommsZone = (f: CommsDegradationZoneFeature): void => {
        logAction(
          'click_comms_degradation_zone',
          { zone_id: f.properties.id, estimate_confidence: f.properties.estimate_confidence },
          buildContext(),
        );
        propsRef.current.onSelectCommsDegradationZone(f);
      };
      // Fire Risk entities come from a GeoJsonDataSource, so their entity.id
      // strings don't follow the `prefix:uuid` scheme — match by entity object
      // identity first, before the prefix gate below.
      const fire = picked ? firesLayer.pick(picked.id) : null;
      if (fire) {
        logAction(
          'click_fire',
          {
            fire_id: fire.properties.id,
            source: fire.properties.source,
            stage_of_control: fire.properties.stage_of_control_status,
          },
          buildContext(),
        );
        propsRef.current.onSelectFire(fire);
        // R.3.1 — zoom to a ~20 km incident viewport + log the navigation.
        {
          const [flon, flat] = fire.properties.centroid;
          propsRef.current.flyControllerRef.current?.flyToIncident(flat, flon);
          logAction(
            'zoom_to_incident',
            { entity_id: fire.properties.id, entity_type: 'active_fire' },
            buildContext(),
          );
        }
        return;
      }
      // High-risk hotspot entities (E.2.3) also come from a CustomDataSource, so
      // match by entity identity before the prefix gate, same as fires.
      const hotspot = picked ? highRiskLayer.pick(picked.id) : null;
      if (hotspot) {
        logAction(
          'click_high_risk_hotspot',
          {
            hotspot_id: hotspot.properties.id,
            response_level: hotspot.properties.response_level,
            confidence: hotspot.properties.confidence,
          },
          buildContext(),
        );
        propsRef.current.onSelectHighRiskHotspot(hotspot);
        // R.3.1 — zoom to a ~20 km incident viewport + log the navigation.
        {
          const [hlon, hlat] = hotspot.geometry.coordinates;
          propsRef.current.flyControllerRef.current?.flyToIncident(hlat, hlon);
          logAction(
            'zoom_to_incident',
            { entity_id: hotspot.properties.id, entity_type: 'high_risk_hotspot' },
            buildContext(),
          );
        }
        return;
      }
      // #370 — Fusion Candidate (below-urgent L1/L2 fused hotspot). It is the SAME
      // high_risk_hotspots row shape as an urgent red, so it opens the SAME side
      // panel via onSelectHighRiskHotspot — but as an AMBIENT below-urgent signal
      // (like a raw hotspot) it does NOT zoom to an incident. Audited under the
      // existing click_high_risk_hotspot verb (no new verb → no migration).
      const fusionCandidate = picked ? fusionCandidatesLayer.pick(picked.id) : null;
      if (fusionCandidate) {
        logAction(
          'click_high_risk_hotspot',
          {
            hotspot_id: fusionCandidate.properties.id,
            response_level: fusionCandidate.properties.response_level,
            confidence: fusionCandidate.properties.confidence,
            surface: 'fusion_candidate',
          },
          buildContext(),
        );
        propsRef.current.onSelectHighRiskHotspot(fusionCandidate);
        return;
      }
      // Extreme Weather Warning CAP polygons (EWW) — also a GeoJsonDataSource, so
      // match by entity identity before the prefix gate. No incident zoom: CAP
      // areas are province-scale regions, not a 20 km point incident.
      const capAlert = picked ? extremeWeatherLayer.pick(picked.id) : null;
      if (capAlert) {
        selectCapAlert(capAlert);
        return;
      }
      // Public-ArcGIS category features (#213) — GeoJsonDataSource layers, so match by
      // entity identity before the prefix gate. The manager resolves which category
      // owns the entity; the audit verb + payload come from that category's config
      // (evacuation keeps its #204 verbs; others share the generic verb). No incident
      // zoom: these are regional areas/points, not point incidents.
      const arcgisHit = picked ? arcgisLayers.pick(picked.id) : null;
      if (arcgisHit) {
        logAction(
          arcgisHit.config.auditClickVerb,
          {
            feature_id: arcgisHit.feature.properties.id,
            category: arcgisHit.feature.properties.category,
            severity: arcgisHit.feature.properties.severity,
            event_type: arcgisHit.feature.properties.event_type,
          },
          buildContext(),
        );
        propsRef.current.onSelectArcgisFeature(arcgisHit.feature);
        return;
      }
      // #445-(3) — operator-declared evac orders (a GeoJsonDataSource): an AMBIENT
      // operator object → its detail panel (escalate / rescind). No incident zoom; the
      // mutations are audited server-side, so the click itself isn't.
      const operatorEvacHit = picked ? operatorEvacLayer.pick(picked.id) : null;
      if (operatorEvacHit) {
        propsRef.current.onSelectOperatorEvacOrder(operatorEvacHit);
        return;
      }
      // L.2.2 — smoke_detected anomaly (a CustomDataSource): a queue incident, so
      // it flies to a ~20 km viewport like a hotspot.
      const smokeAnomaly = picked ? smokeAnomaliesLayer.pick(picked.id) : null;
      if (smokeAnomaly) {
        logAction(
          'click_smoke_detection_anomaly',
          { anomaly_id: smokeAnomaly.properties.id, camera_id: smokeAnomaly.properties.camera_id },
          buildContext(),
        );
        propsRef.current.onSelectSmokeAnomaly(smokeAnomaly);
        {
          const [slon, slat] = smokeAnomaly.geometry.coordinates;
          propsRef.current.flyControllerRef.current?.flyToIncident(slat, slon);
          logAction(
            'zoom_to_incident',
            { entity_id: smokeAnomaly.properties.id, entity_type: 'smoke_detected' },
            buildContext(),
          );
        }
        return;
      }
      // Ice Storm Fusion Anomaly (a CustomDataSource): a queue incident, so it flies
      // to a ~20 km viewport like a hotspot.
      const iceAnomaly = picked ? iceAnomaliesLayer.pick(picked.id) : null;
      if (iceAnomaly) {
        logAction(
          'click_ice_storm_anomaly',
          { anomaly_id: iceAnomaly.properties.id },
          buildContext(),
        );
        propsRef.current.onSelectIceAnomaly(iceAnomaly);
        {
          const [ilon, ilat] = iceAnomaly.geometry.coordinates;
          propsRef.current.flyControllerRef.current?.flyToIncident(ilat, ilon);
          logAction(
            'zoom_to_incident',
            { entity_id: iceAnomaly.properties.id, entity_type: 'ice_storm' },
            buildContext(),
          );
        }
        return;
      }
      // Flood Fusion Anomaly (a CustomDataSource): a queue incident, so it flies to a
      // ~20 km viewport like a hotspot/ice anomaly.
      const floodAnomaly = picked ? floodAnomaliesLayer.pick(picked.id) : null;
      if (floodAnomaly) {
        logAction(
          'click_flood_anomaly',
          {
            anomaly_id: floodAnomaly.properties.id,
            station: floodAnomaly.properties.station_number,
          },
          buildContext(),
        );
        propsRef.current.onSelectFloodAnomaly(floodAnomaly);
        {
          const [flon, flat] = floodAnomaly.geometry.coordinates;
          propsRef.current.flyControllerRef.current?.flyToIncident(flat, flon);
          logAction(
            'zoom_to_incident',
            { entity_id: floodAnomaly.properties.id, entity_type: 'flood' },
            buildContext(),
          );
        }
        return;
      }
      // Hydrometric Gauges (a CustomDataSource): AMBIENT detect signal, NOT an
      // actionable incident — open the detail panel, no incident zoom.
      const gauge = picked ? hydrometricGaugesLayer.pick(picked.id) : null;
      if (gauge) {
        propsRef.current.onSelectHydrometricGauge(gauge);
        return;
      }
      // E.W.0.5 — raw hotspot (a CustomDataSource): AMBIENT pre-fusion signal,
      // NOT an actionable incident — open the detail panel, no incident zoom.
      const rawHotspot = picked ? rawHotspotsLayer.pick(picked.id) : null;
      if (rawHotspot) {
        logAction(
          'click_raw_hotspot',
          { hotspot_id: rawHotspot.properties.id, source: rawHotspot.properties.source },
          buildContext(),
        );
        propsRef.current.onSelectRawHotspot(rawHotspot);
        return;
      }
      // E.W.5 — camera smoke detection (a CustomDataSource): AMBIENT pre-fusion
      // camera-CV signal, NOT an actionable incident — open the detail panel, no
      // incident zoom. A pick can land on the dot OR the camera glyph (both map back).
      const cameraSmoke = picked ? cameraSmokeLayer.pick(picked.id) : null;
      if (cameraSmoke) {
        logAction(
          'click_camera_smoke',
          { detection_id: cameraSmoke.properties.id, camera_id: cameraSmoke.properties.camera_id },
          buildContext(),
        );
        propsRef.current.onSelectCameraSmoke(cameraSmoke);
        return;
      }
      // Power Outages (a CustomDataSource): AMBIENT operational signal, NOT an
      // actionable incident — open the detail panel, no incident zoom. A pick can
      // land on the dot OR the ground-clamped service-area polygon (both map back).
      const outage = picked ? powerOutagesLayer.pick(picked.id) : null;
      if (outage) {
        selectOutage(outage);
        return;
      }
      // Road Closures (a CustomDataSource): AMBIENT operational signal, NOT an
      // actionable incident — open the detail panel, no incident zoom. A pick can
      // land on the dot OR the ground-clamped affected-segment line (both map back).
      const closure = picked ? roadClosuresLayer.pick(picked.id) : null;
      if (closure) {
        selectRoadClosure(closure);
        return;
      }
      // Estimated Comms Degradation (#109, a CustomDataSource): AMBIENT estimate,
      // NOT an actionable incident — open the detail panel, no incident zoom. A pick
      // can land on the zone polygon OR its centroid label (both map back).
      const commsZone = picked ? commsDegradationZonesLayer.pick(picked.id) : null;
      if (commsZone) {
        selectCommsZone(commsZone);
        return;
      }
      // L.2.2 — drone coverage (base marker or its coverage ellipse). Ambient
      // asset: open the detail panel, no incident zoom.
      const drone = picked ? droneCoverageLayer.pick(picked.id) : null;
      if (drone) {
        logAction(
          'click_drone_coverage',
          { drone_id: drone.properties.id, name: drone.properties.name },
          buildContext(),
        );
        propsRef.current.onSelectDroneCoverage(drone);
        return;
      }
      // W.2 — community (a CustomDataSource): ambient register, no fly. Not
      // click-audited (W.2 is migration-free; the keystone mutations a community
      // drives — declare_headcount / wellness checks — are audited server-side).
      const community = picked ? communitiesLayer.pick(picked.id) : null;
      if (community) {
        propsRef.current.onSelectCommunity(community);
        return;
      }
      // L.2.2 — marine vessel (a CustomDataSource): ambient register, no fly.
      const vessel = picked ? marineLayer.pick(picked.id) : null;
      if (vessel) {
        logAction(
          'click_marine_vessel',
          { vessel_id: vessel.properties.id, mmsi: vessel.properties.mmsi },
          buildContext(),
        );
        propsRef.current.onSelectMarineVessel(vessel);
        return;
      }
      // R.4.2 — staging area (a CustomDataSource): ambient register, no fly. Not
      // click-audited (ambient logistics context; its mutations — declare / close /
      // stage — are audited server-side).
      const stagingArea = picked ? stagingAreasLayer.pick(picked.id) : null;
      if (stagingArea) {
        propsRef.current.onSelectStagingArea(stagingArea);
        return;
      }
      // R5 Slice 3 (#507) — Area Command marker/badge (a CustomDataSource): ambient
      // register, no fly. Click opens the parent incident's expand panel. Not
      // click-audited (its mutations — establish / place / remove — are audited
      // server-side; adding a click verb would need a migration, out of scope).
      const areaCommand = picked ? areaCommandLayer.pick(picked.id) : null;
      if (areaCommand) {
        propsRef.current.onSelectAreaCommand(areaCommand);
        return;
      }
      // Task #64 — sector polygon (a CustomDataSource): operator-drawn zone, no
      // fly. Ambient register; the read-only rollup view carries no audit verb
      // (the mutations — draw / edit / close — are audited server-side).
      const sector = picked ? sectorsLayer.pick(picked.id) : null;
      if (sector) {
        propsRef.current.onSelectSector(sector);
        return;
      }
      const entityId =
        picked && picked.id && typeof picked.id.id === 'string' ? picked.id.id : null;
      // #195 — camera cluster: a co-located cluster (zoom can't separate it) drops
      // a selectable "N cameras here" list into the persistent rail; any other
      // cluster zooms in toward its centroid so it breaks apart (zoom expands). Not
      // click-audited (a UI navigation aid; the chosen camera logs click_camera).
      if (entityId && entityId.startsWith('cameracluster:')) {
        const cl = cameraClusters.get(entityId.slice('cameracluster:'.length))?.cluster;
        if (cl) {
          if (cl.coLocated) {
            propsRef.current.onSelectCameraCluster(cl.members);
          } else {
            const height = nextZoomHeight(viewer.camera.positionCartographic.height);
            viewer.camera.flyTo({
              destination: Cesium.Cartesian3.fromDegrees(cl.lon, cl.lat, height),
              duration: 0.5,
            });
          }
        }
        return;
      }
      // Empty globe OR a decorative pick (province boundary / city label, which
      // are context-only — §5.6/§5.7) clears the selection, exactly as clicking
      // bare globe does. Only the known selectable prefixes below open a panel.
      const SELECTABLE = [
        'camera:',
        'cone:',
        'glow:',
        'camglyph:',
        'popoutring:',
        'sensor:',
        'asset:',
        'aircraft:',
      ];
      if (!entityId || !SELECTABLE.some((p) => entityId.startsWith(p))) {
        // #474 — whole-footprint zone fallback (BEFORE clearing). The topmost pick
        // resolved to nothing selectable (bare globe / a decorative province boundary
        // or city label sitting over a zone), but a click ANYWHERE inside a coloured
        // warning zone must open it. The zone FILLS are big ground-clamped / height-0
        // areas a single scene.pick can shadow; scan the drill stack (front-to-back)
        // for the frontmost zone feature and select it. Safe: every higher-priority
        // marker floats (disableDepthTest) and would have BEEN the topmost pick
        // (handled above), so this only rescues an otherwise-dead click on bare fill.
        // Zone priority follows the cascade order: CAP > outage > road > comms.
        const zoneHit = firstClaimedZonePick(drilled, [
          (e) => extremeWeatherLayer.pick(e) != null,
          (e) => powerOutagesLayer.pick(e) != null,
          (e) => roadClosuresLayer.pick(e) != null,
          (e) => commsDegradationZonesLayer.pick(e) != null,
        ]);
        if (zoneHit) {
          const e = zoneHit.id;
          const capFill = extremeWeatherLayer.pick(e);
          if (capFill) {
            selectCapAlert(capFill);
            return;
          }
          const outageFill = powerOutagesLayer.pick(e);
          if (outageFill) {
            selectOutage(outageFill);
            return;
          }
          const closureHit = roadClosuresLayer.pick(e);
          if (closureHit) {
            selectRoadClosure(closureHit);
            return;
          }
          const commsFill = commsDegradationZonesLayer.pick(e);
          if (commsFill) {
            selectCommsZone(commsFill);
            return;
          }
        }
        propsRef.current.onSelectCamera(null);
        propsRef.current.onSelectSensor(null);
        propsRef.current.onSelectAsset(null);
        propsRef.current.onSelectAircraft(null);
        propsRef.current.onSelectFire(null);
        propsRef.current.onSelectHighRiskHotspot(null);
        propsRef.current.onSelectRawHotspot(null);
        propsRef.current.onSelectCameraSmoke(null);
        propsRef.current.onSelectUtilityOutage(null);
        propsRef.current.onSelectRoadClosure(null);
        propsRef.current.onSelectArcgisFeature(null);
        propsRef.current.onSelectCommsDegradationZone(null);
        propsRef.current.onSelectCapAlert(null);
        propsRef.current.onSelectSmokeAnomaly(null);
        propsRef.current.onSelectIceAnomaly(null);
        propsRef.current.onSelectDroneCoverage(null);
        propsRef.current.onSelectMarineVessel(null);
        propsRef.current.onSelectCommunity(null);
        propsRef.current.onSelectStagingArea(null);
        propsRef.current.onSelectSector(null);
        return;
      }
      // Each branch calls only its own setter — App holds a single `selected`,
      // so picking one kind naturally replaces any prior selection without
      // cross-clearing (which would race the setState calls).
      if (
        entityId.startsWith('camera:') ||
        entityId.startsWith('cone:') ||
        entityId.startsWith('glow:') ||
        entityId.startsWith('camglyph:') ||
        entityId.startsWith('popoutring:')
      ) {
        const uuid = entityId.split(':')[1]!;
        const cam = cameras.get(uuid)?.feature ?? null;
        if (cam)
          logAction(
            'click_camera',
            { camera_id: uuid, source: cam.properties.source },
            buildContext(),
          );
        propsRef.current.onSelectCamera(cam);
      } else if (entityId.startsWith('sensor:')) {
        const uuid = entityId.split(':')[1]!;
        const s = sensors.get(uuid)?.feature ?? null;
        if (s)
          logAction(
            'click_sensor',
            { sensor_id: uuid, source: s.properties.source },
            buildContext(),
          );
        propsRef.current.onSelectSensor(s);
      } else if (entityId.startsWith('asset:')) {
        const uuid = entityId.slice('asset:'.length);
        const a = assets.get(uuid)?.feature ?? null;
        if (a)
          logAction(
            'click_asset',
            { asset_id: uuid, asset_class: a.properties.asset_class },
            buildContext(),
          );
        propsRef.current.onSelectAsset(a);
      } else if (entityId.startsWith('aircraft:')) {
        const uuid = entityId.slice('aircraft:'.length);
        const ac = aircraft.get(uuid)?.feature ?? null;
        if (ac)
          logAction(
            'click_aircraft',
            { aircraft_id: uuid, role: ac.properties.aircraft_role },
            buildContext(),
          );
        propsRef.current.onSelectAircraft(ac);
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    // Hover feedback for cones (Phase V.2 §2): track which cone the cursor is
    // over so its material alpha bumps +20%. The cone materials read
    // `hoveredConeId` each frame, so we only update the shared var here.
    handler.setInputAction((movement: Cesium.ScreenSpaceEventHandler.MotionEvent) => {
      const picked = viewer.scene.pick(movement.endPosition);
      const id = picked && picked.id && typeof picked.id.id === 'string' ? picked.id.id : null;
      hoveredConeId = id && id.startsWith('cone:') ? id.slice('cone:'.length) : null;
      // OWNERSHIP HOVER — an owned fire's lead organization + Response Level without a click, at
      // any zoom. Fire entities (GeoJsonDataSource) carry the feature property bag incl.
      // owned_incident; other layers' entities do not, so this naturally scopes to fires.
      let bag: Record<string, unknown> | undefined;
      try {
        bag = picked?.id?.properties?.getValue?.(Cesium.JulianDate.now());
      } catch {
        bag = undefined;
      }
      const owned = bag?.owned_incident as
        | { lead_organization_name?: string | null; response_level?: number | null }
        | null
        | undefined;
      if (owned?.lead_organization_name) {
        setOwnerTip({
          text: ownershipHoverLabel(owned.lead_organization_name, owned.response_level ?? null),
          x: movement.endPosition.x,
          y: movement.endPosition.y,
        });
      } else {
        setOwnerTip(null);
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    // Double-click to zoom in one tier (Stage 5 §5.1). Cesium wires no
    // double-click action by default. We dive to ~half the current altitude,
    // recentred on the ground point under the cursor so the double-clicked
    // feature moves toward centre as we descend. Single-click selection is
    // untouched — a double-click also emits LEFT_CLICKs, but App holds a single
    // `selected`, so re-selecting the same entity is idempotent (no double-fire).
    handler.setInputAction((event: Cesium.ScreenSpaceEventHandler.PositionedEvent) => {
      // Task #64 — the draw tool owns double-click (= finish polygon) while active.
      if (sectorDraw.isActive()) return;
      if (evacDraw.isActive()) return;
      // W.2 editor — don't zoom while placing a community point.
      if (communityPlace.isActive()) return;
      const height = nextZoomHeight(viewer.camera.positionCartographic.height);
      const ray = viewer.camera.getPickRay(event.position);
      const ground =
        (ray && viewer.scene.globe.pick(ray, viewer.scene)) ||
        viewer.camera.pickEllipsoid(event.position);
      const carto =
        (ground && Cesium.Cartographic.fromCartesian(ground)) ?? viewer.camera.positionCartographic;
      viewer.camera.flyTo({
        destination: Cesium.Cartesian3.fromRadians(carto.longitude, carto.latitude, height),
        duration: 0.5,
      });
    }, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

    applyVisibilityRef.current = applyVisibility;
    refreshRef.current = (): void => {
      void refresh();
    };
    refreshAssetsRef.current = refreshAssets;
    applyAssetVisibilityRef.current = applyAssetVisibility;
    refreshAircraftRef.current = refreshAircraft;
    applyAircraftVisibilityRef.current = applyAircraftVisibility;

    // Drive the clock to wall time every frame so SampledPositionProperty
    // interpolation advances at framerate (D2 §3.3), and re-evaluate the
    // aircraft LOD on a throttle (both aircraft and camera move continuously).
    let lastLodAt = 0;
    const onPreUpdate = (): void => {
      viewer.clock.currentTime = Cesium.JulianDate.now();
      const ms = performance.now();
      if (ms - lastLodAt >= AIRCRAFT_LOD_INTERVAL_MS) {
        lastLodAt = ms;
        if (aircraft.size > 0) updateAircraftLod();
      }
    };
    viewer.scene.preUpdate.addEventListener(onPreUpdate);

    // Synthetic-track harness (verification only): drive the real renderAircraft
    // path with generated aircraft so motion/LOD/stress are observable without
    // live traffic. Exposed on window; no effect unless explicitly started.
    let syntheticTimer: ReturnType<typeof setInterval> | null = null;
    let syntheticT0 = 0;
    const stopSynthetic = (): void => {
      if (syntheticTimer) clearInterval(syntheticTimer);
      syntheticTimer = null;
    };
    const startSynthetic = (
      count = 120,
      opts: Partial<SyntheticOptions> & { pollMs?: number } = {},
    ): void => {
      stopSynthetic();
      syntheticT0 = performance.now();
      const pollMs = opts.pollMs ?? 3000;
      const tick = (): void => {
        const elapsedMs = performance.now() - syntheticT0;
        const features = generateSyntheticAircraft({
          count,
          elapsedMs,
          nowIso: new Date().toISOString(),
          center: opts.center,
          spreadDeg: opts.spreadDeg,
        });
        renderAircraft(features);
      };
      tick();
      syntheticTimer = setInterval(tick, pollMs);
    };
    (window as unknown as { __tsrAircraftSim?: unknown }).__tsrAircraftSim = {
      start: startSynthetic,
      stop: stopSynthetic,
      render: renderAircraft,
    };
    applyFireVisibilityRef.current = applyFireVisibility;
    applyHighRiskVisibilityRef.current = applyHighRiskVisibility;
    applyRawHotspotsVisibilityRef.current = applyRawHotspotsVisibility;
    applyFusionCandidatesVisibilityRef.current = applyFusionCandidatesVisibility;
    applyCameraSmokeVisibilityRef.current = applyCameraSmokeVisibility;
    applyPowerOutagesVisibilityRef.current = applyPowerOutagesVisibility;
    applyRoadClosuresVisibilityRef.current = applyRoadClosuresVisibility;
    applyRoadClosuresRestrictionsRef.current = applyRoadClosuresRestrictions;
    applyCommsDegradationVisibilityRef.current = applyCommsDegradationVisibility;
    applyExtremeWeatherVisibilityRef.current = applyExtremeWeatherVisibility;
    applyArcgisVisibilityRef.current = applyArcgisVisibility;
    applyIncidentScopedLayersRef.current = applyIncidentScopedLayers;
    applyBoundaryVisibilityRef.current = applyBoundaryVisibility;
    applyFwiVisibilityRef.current = applyFwiVisibility;
    applyWmsOverlaysVisibilityRef.current = applyWmsOverlaysVisibility;
    applySmokeAnomalyVisibilityRef.current = applySmokeAnomalyVisibility;
    applyIceAnomalyVisibilityRef.current = applyIceAnomalyVisibility;
    applyFloodAnomalyVisibilityRef.current = applyFloodAnomalyVisibility;
    applyHydrometricGaugesVisibilityRef.current = applyHydrometricGaugesVisibility;
    applyFloodInundationVisibilityRef.current = applyFloodInundationVisibility;
    applyCameraViewshedVisibilityRef.current = applyCameraViewshedVisibility;
    applyViewshedCameraRef.current = applyViewshedCamera;
    applyMarineVisibilityRef.current = applyMarineVisibility;
    applyDroneCoverageVisibilityRef.current = applyDroneCoverageVisibility;
    applyCommunityVisibilityRef.current = applyCommunityVisibility;
    recolorCommunitiesRef.current = recolorCommunities;
    applyStagingAreasVisibilityRef.current = applyStagingAreasVisibility;
    applyAreaCommandVisibilityRef.current = applyAreaCommandVisibility;
    // Task #64 — expose the sector draw tool + layer refresh to App's toolbar.
    propsRef.current.sectorControllerRef.current = {
      startDraw: () => sectorDraw.start(),
      cancelDraw: () => sectorDraw.cancel(),
      isDrawing: () => sectorDraw.isActive(),
      refreshSectors: () => void sectorsLayer.refreshNow(),
    };
    // #445-(3) — expose the evac-area draw tool + layer refresh to App's CommunityBody.
    propsRef.current.evacuationOrderControllerRef.current = {
      startDraw: () => evacDraw.start(),
      cancelDraw: () => evacDraw.cancel(),
      isDrawing: () => evacDraw.isActive(),
      previewRing: (ring) => evacDraw.preview(ring),
      clearPreview: () => evacDraw.clearPreview(),
      refreshOrders: () => {
        const rect = viewer.camera.computeViewRectangle();
        if (rect) void operatorEvacLayer.refreshNow(rectangleToBbox(rect));
      },
    };
    // W.2 editor — expose the place-a-point tool + community layer refresh to App.
    propsRef.current.communityControllerRef.current = {
      startPlace: () => communityPlace.start(),
      cancelPlace: () => communityPlace.cancel(),
      isPlacing: () => communityPlace.isActive(),
      refreshCommunities: () => {
        const rect = viewer.camera.computeViewRectangle();
        if (rect) void communitiesLayer.refreshNow(rectangleToBbox(rect));
      },
    };
    // #177 — expose the flood-inundation level control + fly-to to App.
    propsRef.current.floodInundationControllerRef.current = {
      setLevelDepth: (depthM: number) => floodInundationLayer.setLevelDepth(depthM),
      setPreset: (presetId: string) => floodInundationLayer.setPreset(presetId),
      flyToDemo: () => floodInundationLayer.flyToDemo(),
      isReady: () => floodInundationLayer.isReady(),
    };
    // #179 — operator 3D flythrough (photoreal tiles on demand, released on end).
    const flythrough = installFlythrough(viewer);
    if (propsRef.current.flythroughControllerRef) {
      propsRef.current.flythroughControllerRef.current = {
        start: (target, onEnded) => flythrough.start(target, onEnded),
        stop: () => flythrough.stop(),
        isActive: () => flythrough.isActive(),
      };
    }
    // #180 — AAR time replay (historical entities + view capture/restore).
    const aarReplay = installAarReplay(viewer as unknown as AarReplayViewer);
    if (propsRef.current.aarReplayControllerRef) {
      propsRef.current.aarReplayControllerRef.current = {
        start: (data, onEnded) => aarReplay.start(data, onEnded),
        setTime: (tMs) => aarReplay.setTime(tMs),
        stop: () => aarReplay.stop(),
        isActive: () => aarReplay.isActive(),
      };
    }
    // Apply the initial FWI toggle state (in case it was persisted ON).
    applyFwiVisibility();
    // Apply the initial WMS-overlay toggle states (in case any was persisted ON).
    applyWmsOverlaysVisibility();
    // Apply the initial flood-inundation toggle state (in case it was persisted ON).
    applyFloodInundationVisibility();
    // Apply the initial camera-viewshed toggle state (in case it was persisted ON).
    applyCameraViewshedVisibility();
    pulseRef.current = applyPulse;

    (window as unknown as { __tsrViewer?: unknown }).__tsrViewer = viewer;
    // #270 QA hook: live pump counters (pumpRenders/kickRenders/faults) for the
    // occluded-load verification recipe — pairs with __tsrViewer frameNumber.
    (window as unknown as { __tsrOcclusionPump?: unknown }).__tsrOcclusionPump = occlusionPump;

    // Stage 5 context layers. City labels are synchronous local data; boundaries
    // fetch a GeoJSON, so guard the resolve against an unmount that races it.
    cityLabels = installCityLabels(viewer);
    void installProvincialBoundaries(viewer)
      .then((handle) => {
        if (destroyed) {
          try {
            handle.destroy();
          } catch {
            /* viewer already torn down — nothing to remove */
          }
          return;
        }
        boundaryLayer = handle;
        // Apply the current toggle state in case the operator turned boundaries
        // off before the async GeoJSON resolved.
        handle.setVisible(propsRef.current.showProvincialBoundaries);
      })
      .catch((err) => console.error('provincial boundaries failed to load', err));

    void refresh();
    // Independent aircraft poll so new server truths arrive without a map move.
    aircraftTimer = setInterval(() => refreshAircraft(), AIRCRAFT_POLL_MS);
    // Independent anomaly-lifecycle poll (Colour Law 3 / #129 re-alarm) — see
    // refreshAnomalies. Stable view → ack/re-alarm/clear surface without a nudge.
    anomalyTimer = setInterval(() => refreshAnomalies(), ANOMALY_POLL_MS);
    // WALL profile (#132): re-poll cameras + OC-FUL fires + CAP + anomalies on the
    // wall cadence so a fixed, unattended view stays current (anomalies already poll
    // at 45s; this adds the camera/fire/CAP families the bbox-driven path would miss).
    if (propsRef.current.autoRefreshMs && propsRef.current.autoRefreshMs > 0) {
      wallTimer = setInterval(() => {
        const rect = viewer.camera.computeViewRectangle();
        void refresh();
        if (rect) {
          const bbox = rectangleToBbox(rect);
          if (propsRef.current.showFireRisk) firesLayer.requestRefresh(bbox);
          if (propsRef.current.showExtremeWeatherWarnings) {
            extremeWeatherLayer.requestRefresh(bbox);
          }
        }
        refreshAnomalies();
      }, propsRef.current.autoRefreshMs);
    }

    const flyControllerRef = propsRef.current.flyControllerRef;
    const sectorControllerRef = propsRef.current.sectorControllerRef;
    const communityControllerRef = propsRef.current.communityControllerRef;
    const floodInundationControllerRef = propsRef.current.floodInundationControllerRef;
    const flythroughControllerRef = propsRef.current.flythroughControllerRef;
    const aarReplayControllerRef = propsRef.current.aarReplayControllerRef;
    return () => {
      destroyed = true;
      // Dispose context layers before viewer.destroy() tears the scene down.
      boundaryLayer?.destroy();
      cityLabels?.destroy();
      firesLayer.destroy();
      highRiskLayer.destroy();
      rawHotspotsLayer.destroy();
      fusionCandidatesLayer.destroy();
      cameraSmokeLayer.destroy();
      powerOutagesLayer.destroy();
      roadClosuresLayer.destroy();
      commsDegradationZonesLayer.destroy();
      extremeWeatherLayer.destroy();
      arcgisLayers.destroy();
      smokeAnomaliesLayer.destroy();
      iceAnomaliesLayer.destroy();
      floodAnomaliesLayer.destroy();
      hydrometricGaugesLayer.destroy();
      marineLayer.destroy();
      droneCoverageLayer.destroy();
      communitiesLayer.destroy();
      stagingAreasLayer.destroy();
      areaCommandLayer.destroy();
      sectorsLayer.destroy();
      sectorDraw.destroy();
      operatorEvacLayer.destroy();
      evacDraw.destroy();
      communityPlace.destroy();
      fwiLayer.destroy();
      wmsOverlays.destroy();
      // #177 — restore the base map (terrain/depth-test) + drop the buildings/water
      // before the viewer is torn down.
      floodInundationLayer.destroy();
      // #178 — restore the base map (terrain/depth-test) + drop the footprint.
      cameraViewshedLayer.destroy();
      // #179 — release any in-flight flythrough's tiles + terrain hold.
      flythrough.destroy();
      // #180 — drop any replay entities before the viewer is torn down.
      aarReplay.destroy();
      // #270 — stop the occluded-window pump (visibility listener + timer).
      occlusionPump.destroy();
      // #259 — stop the imagery-readiness watcher (tileLoadProgress listener).
      imageryReadiness.destroy();
      // #309 — stop the device-pixel-ratio watcher (matchMedia listener).
      stopDprWatch();
      if (debounceTimer) clearTimeout(debounceTimer);
      if (panZoomTimer) clearTimeout(panZoomTimer);
      if (aircraftTimer) clearInterval(aircraftTimer);
      stopSynthetic();
      viewer.scene.preUpdate.removeEventListener(onPreUpdate);
      if (anomalyTimer) clearInterval(anomalyTimer);
      if (wallTimer) clearInterval(wallTimer);
      if (inflightCameras) inflightCameras.abort();
      if (inflightSensors) inflightSensors.abort();
      if (inflightAssets) inflightAssets.abort();
      if (inflightAircraft) inflightAircraft.abort();
      removeMoveEnd();
      removeMoveEndLog();
      removeBadgeRecompute();
      clearInterval(badgeRecomputeTimer);
      handler.destroy();
      viewer.destroy();
      applyVisibilityRef.current = () => undefined;
      pulseRef.current = () => undefined;
      refreshAssetsRef.current = () => undefined;
      applyAssetVisibilityRef.current = () => undefined;
      refreshAircraftRef.current = () => undefined;
      applyAircraftVisibilityRef.current = () => undefined;
      applyFireVisibilityRef.current = () => undefined;
      applyHighRiskVisibilityRef.current = () => undefined;
      applyRawHotspotsVisibilityRef.current = () => undefined;
      applyFusionCandidatesVisibilityRef.current = () => undefined;
      applyCameraSmokeVisibilityRef.current = () => undefined;
      applyPowerOutagesVisibilityRef.current = () => undefined;
      applyRoadClosuresVisibilityRef.current = () => undefined;
      applyRoadClosuresRestrictionsRef.current = () => undefined;
      applyCommsDegradationVisibilityRef.current = () => undefined;
      applyExtremeWeatherVisibilityRef.current = () => undefined;
      applyArcgisVisibilityRef.current = () => undefined;
      applyIncidentScopedLayersRef.current = () => undefined;
      applyFwiVisibilityRef.current = () => undefined;
      applyWmsOverlaysVisibilityRef.current = () => undefined;
      applySmokeAnomalyVisibilityRef.current = () => undefined;
      applyIceAnomalyVisibilityRef.current = () => undefined;
      applyFloodAnomalyVisibilityRef.current = () => undefined;
      applyHydrometricGaugesVisibilityRef.current = () => undefined;
      applyFloodInundationVisibilityRef.current = () => undefined;
      applyCameraViewshedVisibilityRef.current = () => undefined;
      applyViewshedCameraRef.current = () => undefined;
      applyMarineVisibilityRef.current = () => undefined;
      applyDroneCoverageVisibilityRef.current = () => undefined;
      applyCommunityVisibilityRef.current = () => undefined;
      recolorCommunitiesRef.current = () => undefined;
      applyStagingAreasVisibilityRef.current = () => undefined;
      applyAreaCommandVisibilityRef.current = () => undefined;
      (window as unknown as { __tsrViewer?: unknown }).__tsrViewer = undefined;
      (window as unknown as { __tsrOcclusionPump?: unknown }).__tsrOcclusionPump = undefined;
      (window as unknown as { __tsrRenderResolution?: unknown }).__tsrRenderResolution = undefined;
      (window as unknown as { __tsrAircraft?: unknown }).__tsrAircraft = undefined;
      flyControllerRef.current = null;
      sectorControllerRef.current = null;
      communityControllerRef.current = null;
      floodInundationControllerRef.current = null;
      if (flythroughControllerRef) flythroughControllerRef.current = null;
      if (aarReplayControllerRef) aarReplayControllerRef.current = null;
    };
  }, []);

  // Toggle visibility without re-fetching when prefs change.
  useEffect(() => {
    applyVisibilityRef.current();
  }, [props.showCameras, props.showSensors]);

  // Smoke-priority filter is server-side, so flipping it requires a
  // fresh fetch (unlike the local layer visibility toggle).
  useEffect(() => {
    refreshRef.current();
  }, [props.smokePriorityOnly]);

  // Asset sublayer toggles change the server-side asset_class filter → refetch.
  useEffect(() => {
    refreshAssetsRef.current();
  }, [
    props.showFireStations,
    props.showWildfireBases,
    props.showAviationBases,
    props.showOntarioCorps,
  ]);

  // Hiding/showing individual Ontario Corps partners is client-side only.
  useEffect(() => {
    applyAssetVisibilityRef.current();
  }, [props.hiddenPartners]);

  // Toggling the Live Aircraft layer on/off triggers a fetch (on) or teardown
  // (off); both run through refreshAircraft.
  useEffect(() => {
    refreshAircraftRef.current();
  }, [props.showLiveAircraft]);

  // Hiding/showing individual aircraft roles is client-side visibility only.
  useEffect(() => {
    applyAircraftVisibilityRef.current();
  }, [props.hiddenAircraftRoles]);

  // Re-apply marker pulse when the set of open pop-out windows changes.
  useEffect(() => {
    pulseRef.current();
  }, [props.pulsingCameraIds]);

  // Fire Risk toggle: show/hide the GeoJsonDataSource (and fetch on turn-on).
  useEffect(() => {
    applyFireVisibilityRef.current();
  }, [props.showFireRisk]);

  // L.1.2 / L.2.1 — switching the stage or response_type filter (OC-FUL ↔ all
  // stages) while the layer is already visible must re-fetch with the new
  // `?stage=` / `?responseType=` params. applyFire... only fetches when visible,
  // so it's a no-op when the layer is off.
  useEffect(() => {
    applyFireVisibilityRef.current();
  }, [props.fireStages, props.fireResponseTypes]);

  // Provincial Boundaries toggle (Context group, L.1): pure show/hide.
  useEffect(() => {
    applyBoundaryVisibilityRef.current();
  }, [props.showProvincialBoundaries]);

  // High-Risk Hotspots toggle (E.2.3): show/hide + fetch on turn-on.
  useEffect(() => {
    applyHighRiskVisibilityRef.current();
  }, [props.showHighRiskHotspots]);

  // E.W.0.5 — Raw Hot Spots toggle (Fire Risk group): show/hide + fetch on
  // turn-on. The layer self-gates rendering to regional+ zoom.
  useEffect(() => {
    applyRawHotspotsVisibilityRef.current();
  }, [props.showRawHotspots]);

  // #370 — Fusion Candidates toggle (Fire Risk group): show/hide + fetch on
  // turn-on. The layer self-gates rendering to regional+ zoom.
  useEffect(() => {
    applyFusionCandidatesVisibilityRef.current();
  }, [props.showFusionCandidates]);

  // E.W.5 — Camera Smoke Detections toggle (Fire Risk group): show/hide + fetch on
  // turn-on. The layer self-gates rendering to regional+ zoom.
  useEffect(() => {
    applyCameraSmokeVisibilityRef.current();
  }, [props.showCameraSmoke]);

  // Power Outages toggle (Ice Storm Risk group): show/hide + fetch on turn-on.
  // The layer self-gates rendering to regional+ zoom.
  useEffect(() => {
    applyPowerOutagesVisibilityRef.current();
  }, [props.showPowerOutages]);

  // Road Closures toggle (Ice Storm Risk group): show/hide + fetch on turn-on.
  // The layer self-gates rendering to regional+ zoom.
  useEffect(() => {
    applyRoadClosuresVisibilityRef.current();
  }, [props.showRoadClosures]);

  // Road Closures "Restrictions" mute (L.1 sub-toggle): re-render the loaded
  // closures from cache so restriction dots vanish/return without a refetch.
  useEffect(() => {
    applyRoadClosuresRestrictionsRef.current();
  }, [props.roadClosuresRestrictions]);

  // Estimated Comms Degradation toggle (#109, Ice Storm Risk group): show/hide +
  // fetch on turn-on. The layer self-gates rendering to regional+ zoom.
  useEffect(() => {
    applyCommsDegradationVisibilityRef.current();
  }, [props.showCommsDegradationZones]);

  // Extreme Weather Warnings (EWW): show/hide + fetch on turn-on. Driven by the
  // blend rule (urgentAnomalies && extremeWeatherWarnings), so it reacts both to
  // the Layer #2 toggle and the Weather-group mute toggle.
  useEffect(() => {
    applyExtremeWeatherVisibilityRef.current();
  }, [props.showExtremeWeatherWarnings]);

  // Public-ArcGIS category renders (#213): show/hide + fetch on turn-on, across all
  // categories. The visibility map's identity is stable (memoized in App) unless a
  // toggle changes.
  useEffect(() => {
    applyArcgisVisibilityRef.current();
  }, [props.arcgisCategoryVisibility]);

  // #428 — incident-scoped layers (sector overlay incident + operator-evac orders)
  // react to a LIVE change of their driving props. `applyVisibility` re-applies
  // these only on a camera/sensor rebuild, which never happens on the read-only
  // wall — so without this an escalated incident's sectors never surface on an
  // already-mounted wall. On the COP this is redundant with applyVisibility (both
  // idempotent), and makes an operator-evac / incident-selection change immediate.
  useEffect(() => {
    applyIncidentScopedLayersRef.current();
  }, [props.sectorIncidentId, props.showOperatorEvacOrders]);

  // FWI heatmap toggle (Weather group, E.2.2): pure show/hide of the WMS overlay.
  useEffect(() => {
    applyFwiVisibilityRef.current();
  }, [props.showFwiHeatmap]);

  // WMS raster overlays (#212): show/hide each overlay when its toggle changes.
  // The visibility map's identity is stable (memoized in App) unless a toggle flips.
  useEffect(() => {
    applyWmsOverlaysVisibilityRef.current();
  }, [props.wmsOverlayVisibility]);

  // L.2.2 — smoke_detected anomalies ride the Anomaly Queue toggle (same show
  // condition as the fused hotspots).
  useEffect(() => {
    applySmokeAnomalyVisibilityRef.current();
  }, [props.showHighRiskHotspots]);

  // Ice Storm Fusion Anomaly rides the Anomaly Queue toggle.
  useEffect(() => {
    applyIceAnomalyVisibilityRef.current();
  }, [props.showHighRiskHotspots]);

  // Flood Fusion Anomaly rides the Anomaly Queue toggle.
  useEffect(() => {
    applyFloodAnomalyVisibilityRef.current();
  }, [props.showHighRiskHotspots]);

  // Hydrometric Gauges toggle (Flood Risk group): show/hide + fetch on turn-on.
  // The layer self-gates rendering to regional+ zoom.
  useEffect(() => {
    applyHydrometricGaugesVisibilityRef.current();
  }, [props.showHydrometricGauges]);

  // 3D Flood Inundation toggle (#177): enable/disable the terrain + buildings +
  // water mechanic. The layer bounds itself to the demo region.
  useEffect(() => {
    applyFloodInundationVisibilityRef.current();
  }, [props.showFloodInundation]);

  // #178/#262 — the per-operator viewshed-on-select pref (the camera panel's
  // "hide viewshed" affordance): flipping it engages/releases the mechanic for
  // the currently selected camera.
  useEffect(() => {
    applyCameraViewshedVisibilityRef.current();
  }, [props.viewshedOnSelect]);

  // #178/#262 — selection drives the drape: select = acquire terrain + async
  // compute; deselect = clear + release (only acts while the pref is ON).
  useEffect(() => {
    applyViewshedCameraRef.current();
  }, [props.viewshedCamera]);

  // L.2.2 — marine vessels ride the Aircraft & Marine toggle.
  useEffect(() => {
    applyMarineVisibilityRef.current();
  }, [props.showLiveAircraft]);

  // L.2.2 — Drone Operator Coverage toggle; also re-paint the reachability
  // highlight whenever the selected incident point changes.
  useEffect(() => {
    applyDroneCoverageVisibilityRef.current();
  }, [props.showDroneCoverage, props.selectedIncidentPoint]);

  // W.2 — Vulnerable Communities toggle (Flood/Ice-Storm OR): show/hide + fetch.
  useEffect(() => {
    applyCommunityVisibilityRef.current();
  }, [props.showCommunities]);

  // W.2 — recolour community markers in place when the status/badge cache updates.
  useEffect(() => {
    recolorCommunitiesRef.current();
  }, [props.communityStatuses]);

  // #189 — Vulnerability shading sub-toggle: re-apply the chip text + tint from
  // the already-loaded features (no refetch, mirrors the Restrictions re-filter).
  useEffect(() => {
    recolorCommunitiesRef.current();
  }, [props.communityVulnerabilityShading]);

  // R.4.2 — Staging Areas toggle: show/hide + fetch-on-turn-on.
  useEffect(() => {
    applyStagingAreasVisibilityRef.current();
  }, [props.showStagingAreas]);

  // R5 Slice 3 (#507) — Area Command toggle: show/hide + refresh-on-turn-on.
  useEffect(() => {
    applyAreaCommandVisibilityRef.current();
  }, [props.showAreaCommand]);

  // #259 — wrap the Cesium container (which Cesium owns the contents of) in a
  // positioned parent so the cold-boot loading chrome can layer over the canvas.
  // The container keeps its exact `flex-1 h-full` box (the proven sizing); the
  // wrapper is `flex` so the container fills it identically on the COP and wall.
  return (
    <div className="relative flex flex-1 h-full">
      <div ref={containerRef} className="flex-1 h-full" data-testid="map-container" />
      {ownerTip && (
        <div
          data-testid="ownership-hover-tip"
          className="pointer-events-none absolute z-20 rounded bg-slate-950/85 px-2 py-1 text-[11px] font-medium text-slate-100 shadow ring-1 ring-slate-700/60"
          style={{ left: ownerTip.x + 14, top: ownerTip.y + 14 }}
        >
          {ownerTip.text}
        </div>
      )}
      <GlobeBootOverlay ready={imageryReady} />
    </div>
  );
}
